import type { ReactNode, RefObject } from "react";

import type {
  PatientWorkspaceSummaryResponseV1,
  WorkforceSessionSummaryV1,
} from "@amplecare/contracts";
import { designTokens } from "@amplecare/design-tokens";
import {
  Image,
  Modal,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  type LayoutChangeEvent,
} from "react-native";

import { styles } from "../AppShell.styles.js";
import {
  createWebGradientBackgroundStyle,
  createWebOrderStyle,
} from "../shell-primitives.js";
import { LineIcon } from "../line-icons.js";
import {
  patientDesktopPalette,
  patientMobilePalette,
  resolveLegacyCompactLeadText,
  resolvePatientInitials,
  type AppShellTheme,
} from "../shell-core.js";
import { renderPatientDesktopAgentAvatarMedia } from "./patient-assets.js";
import {
  renderPatientLegacyOnboardingOverlay,
  type PatientLegacyOnboardingFlowState,
  type PatientOnboardingLayoutRects,
  type PatientOnboardingRegionKey,
} from "./patient-onboarding.js";
import type {
  PatientMedicationReminderTimeUpdateInput,
} from "./patient-mobile.js";
import {
  formatPatientMobilePreferenceTime,
} from "./patient-mobile.js";
import {
  renderPatientDesktopFooter,
  type PatientSupportModalState,
} from "./patient-support.js";
import {
  type PatientWorkspaceReadAloudControls,
  type PatientWorkspaceVoiceInputControls,
} from "./patient-speech.js";
import type { ChatMessageViewModel } from "../types.js";
import { resolvePatientBrand } from "./brand.js";

const brand = resolvePatientBrand();

type PatientWorkspaceTabId =
  | "overview"
  | "onboarding"
  | "appointments"
  | "procedures"
  | "medications"
  | "documents"
  | "calls"
  | "messages"
  | "settings";

type PatientWorkspaceNavigationItem = {
  badgeText: string | null;
  id: PatientWorkspaceTabId;
  label: string;
};

type PatientMessageCommandInput = {
  bodyText: string;
};

type PatientDesktopPalette = {
  agentBlue: string;
  black: string;
  body: string;
  darkBlue: string;
  inputBackground: string;
  inputBorder: string;
  lightBand: string;
  lightBlue: string;
  lightTile: string;
  line: string;
  linkBlue: string;
  mutedText: string;
  primaryText: string;
  tileSelected: string;
  white: string;
};

type PatientDesktopTableCell = {
  actions?: ReadonlyArray<{
    labelText: string;
    onPress: () => void;
    tone?: "accent" | "success";
  }>;
  onPress?: () => void;
  primaryText: string;
  secondaryText?: string | null;
  tone?: "accent" | null;
};

type PatientDesktopTableColumn = {
  flex: number;
  id: string;
  labelText: string;
};

type PatientDesktopTableRow = {
  cells: PatientDesktopTableCell[];
  id: string;
};

type PatientWorkspaceDetailSectionId =
  PatientWorkspaceSummaryResponseV1["patient"]["detailSections"][number]["id"];
type PatientWorkspaceDetailRow =
  PatientWorkspaceSummaryResponseV1["patient"]["detailSections"][number]["rows"][number];
type PatientWorkspaceDocumentSummary =
  PatientWorkspaceSummaryResponseV1["patient"]["documentSummaries"][number];

type PatientDesktopCareDetailKind =
  | "appointment"
  | "procedure"
  | "medication"
  | "document";
type PatientDesktopClinicalRecordState = {
  bucket: number;
  isHistorical: boolean;
  labelText: string;
  sortTime: number | null;
};

export type PatientDesktopCareDetailModalState = {
  badgeText: string;
  detailRows: ReadonlyArray<{
    id: string;
    labelText: string;
    valueText: string;
  }>;
  itemId: string;
  kind: PatientDesktopCareDetailKind;
  reviewPromptText: string;
  subtitleText: string | null;
  titleText: string;
} | null;

const patientDesktopMedicationReminderOptions: readonly PatientMedicationReminderTimeUpdateInput[] =
  [
    {
      displayTime: "7 AM",
      label: "Morning",
      slotId: "morning",
      wakeUpTime: "07:00",
    },
    {
      displayTime: "12 PM",
      label: "Midday",
      slotId: "midday",
      wakeUpTime: "12:00",
    },
    {
      displayTime: "6 PM",
      label: "Evening",
      slotId: "evening",
      wakeUpTime: "18:00",
    },
  ];

const patientDesktopWakeUpTimeOptions: readonly Pick<
  PatientMedicationReminderTimeUpdateInput,
  "displayTime" | "wakeUpTime"
>[] = [
  {
    displayTime: "7 AM",
    wakeUpTime: "07:00",
  },
  {
    displayTime: "8 AM",
    wakeUpTime: "08:00",
  },
  {
    displayTime: "9 AM",
    wakeUpTime: "09:00",
  },
];

function formatPatientDisplayName(displayName: string | null | undefined): string {
  const trimmedDisplayName = displayName?.trim() ?? "";

  if (trimmedDisplayName.length === 0) {
    return "Patient";
  }

  const commaParts = trimmedDisplayName
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);

  if (commaParts.length === 0) {
    return "Patient";
  }

  if (commaParts.length === 1) {
    return commaParts[0];
  }

  const [lastName, ...rest] = commaParts;
  const givenNames = rest.join(" ");
  return [givenNames, lastName].filter((part) => part.length > 0).join(" ");
}

function resolvePatientWorkspaceGivenName(
  displayName: string | null | undefined,
): string {
  const firstName = formatPatientDisplayName(displayName).split(/\s+/u)[0];

  return firstName && firstName.length > 0 ? firstName : "there";
}

function resolveDocumentBadgeText(labelText: string): string {
  const trimmedLabel = labelText.trim();

  if (trimmedLabel.length === 0) {
    return "DOC";
  }

  const words = trimmedLabel
    .split(/\s+/u)
    .map((word) => word.replace(/[^A-Za-z0-9]/g, ""))
    .filter((word) => word.length > 0);

  if (words.length === 0) {
    return trimmedLabel.slice(0, 3).toUpperCase();
  }

  if (words.length === 1) {
    return words[0].slice(0, 3).toUpperCase();
  }

  return words
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
}

function parseWorkbenchDateTimeText(valueText: string): Date | null {
  const trimmedValue = valueText.trim();

  if (trimmedValue.length === 0) {
    return null;
  }

  const isoMatch = trimmedValue.match(
    /\b(20\d{2})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*(Z|[+-]\d{2}:?\d{2}))?)?\b/u,
  );

  if (isoMatch) {
    const [, year, month, day, hour = "12", minute = "00", second = "00", zone] =
      isoMatch;
    const dateText = zone
      ? `${year}-${month}-${day}T${hour.padStart(2, "0")}:${minute}:${second}${zone}`
      : `${year}-${month}-${day}T${hour.padStart(2, "0")}:${minute}:${second}Z`;
    const parsedDate = new Date(dateText);

    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  const usMatch = trimmedValue.match(/\b(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])\/(20\d{2})\b/u);

  if (!usMatch) {
    return null;
  }

  const [, rawMonth, rawDay, rawYear] = usMatch;
  const afterDateText = trimmedValue.slice(
    (usMatch.index ?? 0) + usMatch[0].length,
  );
  const timeMatch = afterDateText.match(
    /(?:\btime\s*:\s*)?(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\b/iu,
  );
  let hour = timeMatch ? Number.parseInt(timeMatch[1] ?? "12", 10) : 12;
  const minute = timeMatch ? Number.parseInt(timeMatch[2] ?? "0", 10) : 0;
  const meridiem = timeMatch?.[3]?.toLowerCase();

  if (meridiem === "pm" && hour < 12) {
    hour += 12;
  } else if (meridiem === "am" && hour === 12) {
    hour = 0;
  }

  const parsedDate = new Date(
    Number.parseInt(rawYear, 10),
    Number.parseInt(rawMonth, 10) - 1,
    Number.parseInt(rawDay, 10),
    hour,
    Number.isFinite(minute) ? minute : 0,
  );

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

function resolvePatientDocumentType(labelText: string): string {
  const normalized = labelText.trim().toLowerCase();

  if (normalized.includes("consent")) {
    return "Consent";
  }

  if (
    normalized.includes("questionnaire") ||
    normalized.includes("screening") ||
    normalized.includes("asb")
  ) {
    return "Questionnaire";
  }

  if (
    normalized.includes("lab") ||
    normalized.includes("pathology") ||
    normalized.includes("mri") ||
    normalized.includes("report")
  ) {
    return "Lab result";
  }

  if (
    normalized.includes("post-procedure") ||
    normalized.includes("post procedure") ||
    normalized.includes("procedure note")
  ) {
    return "Post-procedure note";
  }

  return "Other";
}

function readPatientWorkspaceDetailRows(
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null,
  sectionId: PatientWorkspaceDetailSectionId,
): PatientWorkspaceDetailRow[] {
  return (
    summary?.detailSections
      .find((section) => section.id === sectionId)
      ?.rows.filter(
        (row) => row.label.trim().length > 0 || row.valueText.trim().length > 0,
      ) ?? []
  );
}

function resolvePatientDesktopClinicalRecordState(
  row: PatientWorkspaceDetailRow,
  sectionId: PatientWorkspaceDetailSectionId,
): PatientDesktopClinicalRecordState {
  const sourceParts = readPatientDesktopSourceParts(row.valueText);
  const sourceStatusText =
    readPatientDesktopDetailPart(sourceParts, "Status")?.toLowerCase() ?? "";
  const isArchived =
    row.clinicalStatus === "archived" ||
    ["archived", "inactive", "discontinued", "cancelled", "canceled"].some(
      (status) => sourceStatusText.includes(status),
    );
  const datePart = sourceParts.find((part) => parseWorkbenchDateTimeText(part));
  const parsedDate = datePart ? parseWorkbenchDateTimeText(datePart) : null;
  const sortTime = parsedDate?.getTime() ?? null;
  const isDatedClinicalRecord =
    sectionId === "appointments" || sectionId === "procedures";
  const isPastDatedRecord =
    isDatedClinicalRecord && sortTime != null && sortTime < Date.now();

  if (isArchived) {
    return {
      bucket: 3,
      isHistorical: true,
      labelText: "Archived",
      sortTime,
    };
  }

  if (isPastDatedRecord) {
    return {
      bucket: 2,
      isHistorical: true,
      labelText: "Past",
      sortTime,
    };
  }

  if (isDatedClinicalRecord && sortTime != null) {
    return {
      bucket: 0,
      isHistorical: false,
      labelText: "Upcoming",
      sortTime,
    };
  }

  return {
    bucket: 1,
    isHistorical: false,
    labelText: sectionId === "medications" ? "Current" : "Active",
    sortTime,
  };
}

function sortPatientDesktopDetailRowsForCareSurface(
  rows: PatientWorkspaceDetailRow[],
  sectionId: PatientWorkspaceDetailSectionId,
): PatientWorkspaceDetailRow[] {
  return rows
    .map((row, index) => ({
      index,
      row,
      state: resolvePatientDesktopClinicalRecordState(row, sectionId),
    }))
    .sort((left, right) => {
      if (left.state.bucket !== right.state.bucket) {
        return left.state.bucket - right.state.bucket;
      }

      if (left.state.sortTime != null && right.state.sortTime != null) {
        return left.state.sortTime - right.state.sortTime;
      }

      return left.index - right.index;
    })
    .map(({ row }) => row);
}

export function renderPatientWorkspaceDesktopShell(input: {
  activeWorkforceSession: WorkforceSessionSummaryV1 | null;
  conversationMessages: ChatMessageViewModel[];
  draftValue: string;
  hasPatientWorkspaceError: boolean;
  isDocumentUploadSubmitting: boolean;
  isOnboardingCommandSubmitting: boolean;
  isPatientWorkspaceLoading: boolean;
  isMessageCommandSubmitting: boolean;
  layout: typeof designTokens.semanticLayout;
  layoutRects: PatientOnboardingLayoutRects;
  legacyHospitalLogoSource: { uri: string };
  navItems: readonly PatientWorkspaceNavigationItem[];
  noticeText: string | null;
  onConversationContentSizeChange: () => void;
  onDownloadDocument: (document: PatientWorkspaceDocumentSummary) => void;
  onDraftChange: (value: string) => void;
  onUploadDocument: () => void;
  onOnboardingBack: () => void;
  onOnboardingNext: () => void;
  onOnboardingRegionLayout: (
    regionKey: PatientOnboardingRegionKey,
    event: LayoutChangeEvent,
  ) => void;
  onOnboardingSkip: () => void;
  onCloseCareDetailModal: () => void;
  onSignOut: () => void;
  onStartOnboarding: () => void;
  onNoticeChange: (value: string | null) => void;
  onOpenCareDetailModal: (
    detail: NonNullable<PatientDesktopCareDetailModalState>,
  ) => void;
  onOpenSupportModal: (input: PatientSupportModalState) => void;
  readAloudControls: PatientWorkspaceReadAloudControls;
  onSendMessage: (input: PatientMessageCommandInput) => Promise<boolean>;
  onUpdateReminderTime: (
    input: PatientMedicationReminderTimeUpdateInput,
  ) => void;
  onboardingFlow: PatientLegacyOnboardingFlowState;
  patientConversationScrollRef: RefObject<ScrollView | null>;
  patientDesktopCareDetailModal: PatientDesktopCareDetailModalState;
  selectedPatientWorkspaceTabId: PatientWorkspaceTabId;
  setSelectedPatientWorkspaceTabId: (value: PatientWorkspaceTabId) => void;
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
  supportModalElement: ReactNode;
  tabContent: ReactNode;
  theme: AppShellTheme;
  tokens: typeof designTokens;
  voiceInputControls: PatientWorkspaceVoiceInputControls;
  viewportHeight: number;
  viewportWidth: number;
}) {
  const {
    activeWorkforceSession,
    conversationMessages,
    draftValue,
    hasPatientWorkspaceError,
    isDocumentUploadSubmitting,
    isOnboardingCommandSubmitting,
    isPatientWorkspaceLoading,
    isMessageCommandSubmitting,
    layout,
    layoutRects,
    legacyHospitalLogoSource,
    navItems,
    noticeText,
    onConversationContentSizeChange,
    onDownloadDocument,
    onDraftChange,
    onUploadDocument,
    onOnboardingBack,
    onOnboardingNext,
    onOnboardingRegionLayout,
    onOnboardingSkip,
    onCloseCareDetailModal,
    onSignOut,
    onStartOnboarding,
    onNoticeChange,
    onOpenCareDetailModal,
    onOpenSupportModal,
    readAloudControls,
    onSendMessage,
    onUpdateReminderTime,
    onboardingFlow,
    patientConversationScrollRef,
    patientDesktopCareDetailModal,
    selectedPatientWorkspaceTabId,
    setSelectedPatientWorkspaceTabId,
    summary,
    supportModalElement,
    tabContent,
    theme,
    tokens,
    voiceInputControls,
    viewportHeight,
    viewportWidth,
  } = input;
  const desktopViewportHeight = Math.max(720, viewportHeight);
  const desktopHorizontalPadding =
    viewportWidth < 960 ? 24 : viewportWidth < 1180 ? 40 : 72;
  const desktopContentMaxWidth = Math.max(
    320,
    Math.min(1280, viewportWidth - desktopHorizontalPadding * 2),
  );
  const desktopHeaderMaxWidth = Math.max(
    320,
    Math.min(1600, viewportWidth - desktopHorizontalPadding * 2),
  );
  const displayName = formatPatientDisplayName(
    summary?.displayName ?? activeWorkforceSession?.displayName ?? "Patient",
  );
  const patientInitials = resolvePatientInitials(displayName);
  const desktopPalette = patientDesktopPalette;
  const desktopMenuItems = (
    [
      ["overview", "Home"],
      ["appointments", "Appointments"],
      ["procedures", "Procedures"],
      ["medications", "Medications"],
      ["documents", "Documents"],
      ["calls", "Calls"],
    ] as const
  )
    .map(([id, label]): PatientWorkspaceNavigationItem | null => {
      const item = navItems.find((navItem) => navItem.id === id);

      return item ? { ...item, label } : null;
    })
    .filter((item): item is PatientWorkspaceNavigationItem => item != null);
  const selectedMenuItem =
    desktopMenuItems.find(
      (item) => item.id === selectedPatientWorkspaceTabId,
    ) ?? null;
  const canOpenMessages = navItems.some((item) => item.id === "messages");
  const desktopMenuTileFlexBasis = `${
    100 / Math.max(desktopMenuItems.length, 1)
  }%` as `${number}%`;
  const statusText = hasPatientWorkspaceError
    ? "The patient workspace could not load the current care summary."
    : isPatientWorkspaceLoading
      ? "Loading your care workspace."
      : null;
  const isDesktopAgentPage = selectedPatientWorkspaceTabId === "messages";

  return (
    <View
      onLayout={(event) => onOnboardingRegionLayout("root", event)}
      style={[
        styles.patientDesktopRoot,
        {
          backgroundColor: desktopPalette.body,
          height: desktopViewportHeight,
          minHeight: desktopViewportHeight,
          position: "relative",
        },
      ]}
    >
      <View
        style={[
          styles.patientDesktopTopBar,
          createWebGradientBackgroundStyle(desktopPalette.shellGradient),
          {
            backgroundColor: desktopPalette.darkBlue,
            minHeight: 54,
          },
        ]}
      >
        <View
          style={[
            styles.patientDesktopTopBarInner,
            {
              maxWidth: desktopHeaderMaxWidth,
            },
          ]}
        >
          <View
            style={[
              styles.brandLockupRow,
              {
                gap: designTokens.spacing.sm,
              },
            ]}
          >
            <Text
              style={[
                styles.navigationText,
                {
                  color: desktopPalette.white,
                  fontFamily: layout.displayFontFamily,
                  fontSize: 16,
                  fontWeight: "700",
                },
              ]}
            >
              {brand.brandName}
            </Text>
          </View>

          <View
            style={[
              styles.patientRowFooter,
              {
                gap: designTokens.spacing.md,
                marginLeft: "auto",
              },
            ]}
          >
            <Pressable
              accessibilityRole="button"
              onPress={() => onStartOnboarding()}
              style={[
                styles.inlinePill,
                {
                  alignItems: "center",
                  backgroundColor: "rgba(255,255,255,0.16)",
                  borderColor: "rgba(255,255,255,0.32)",
                  borderRadius: designTokens.radius.pill,
                  borderWidth: 1,
                  justifyContent: "center",
                  minHeight: 34,
                  paddingHorizontal: designTokens.spacing.lg,
                },
              ]}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: desktopPalette.white,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 12,
                    fontWeight: "700",
                  },
                ]}
              >
                Onboarding guide
              </Text>
            </Pressable>
            <Text
              style={[
                styles.navigationText,
                {
                  color: desktopPalette.white,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 16,
                  letterSpacing: 0,
                },
              ]}
            >
              {displayName}
            </Text>
            <Pressable
              accessibilityLabel="Open profile"
              accessibilityRole="button"
              onPress={() => setSelectedPatientWorkspaceTabId("settings")}
              style={[
                styles.patientAvatar,
                {
                  backgroundColor: patientDesktopPalette.inputBackground,
                  borderColor:
                    selectedPatientWorkspaceTabId === "settings"
                      ? desktopPalette.white
                      : patientDesktopPalette.inputBackground,
                  borderRadius: 18,
                  borderWidth:
                    selectedPatientWorkspaceTabId === "settings" ? 2 : 1,
                },
              ]}
            >
              <Text
                style={[
                  styles.patientAvatarText,
                  {
                    color: desktopPalette.darkBlue,
                    fontFamily: layout.displayFontFamily,
                  },
                ]}
              >
                {patientInitials}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>

      <View
        style={{
          backgroundColor: desktopPalette.body,
          flex: 1,
          flexDirection: "row",
          minHeight: 0,
          position: "relative",
        }}
      >
        {/* Direction 2 — persistent left sidebar navigation */}
        <View
          onLayout={(event) => onOnboardingRegionLayout("nav", event)}
          style={[
            styles.listGroup,
            {
              backgroundColor: desktopPalette.lightBand,
              borderRightColor: desktopPalette.line,
              borderRightWidth: 1,
              gap: designTokens.spacing.xs,
              paddingHorizontal: designTokens.spacing.md,
              paddingTop: designTokens.spacing.lg,
              width: 224,
            },
          ]}
        >
          {desktopMenuItems.map((item) => {
            const isSelected = item.id === selectedPatientWorkspaceTabId;
            const navEmoji =
              item.id === "appointments"
                ? String.fromCodePoint(0x1f4c5)
                : item.id === "procedures"
                  ? String.fromCodePoint(0x2695, 0xfe0f)
                  : item.id === "medications"
                    ? String.fromCodePoint(0x1f48a)
                    : item.id === "documents"
                      ? String.fromCodePoint(0x1f4c4)
                      : item.id === "calls"
                        ? String.fromCodePoint(0x1f4de)
                        : String.fromCodePoint(0x1f3e0);

            return (
              <Pressable
                accessibilityRole="tab"
                accessibilityState={{ selected: isSelected }}
                key={item.id}
                onPress={() => setSelectedPatientWorkspaceTabId(item.id)}
                style={{
                  alignItems: "center",
                  backgroundColor: isSelected
                    ? desktopPalette.white
                    : "transparent",
                  borderLeftColor: isSelected
                    ? desktopPalette.actionBlue
                    : "transparent",
                  borderLeftWidth: 3,
                  borderRadius: designTokens.radius.none,
                  flexDirection: "row",
                  gap: designTokens.spacing.md,
                  minHeight: 46,
                  paddingLeft: 13,
                  paddingRight: 14,
                }}
              >
                <Text style={{ fontSize: 17, textAlign: "center", width: 22 }}>
                  {navEmoji}
                </Text>
                <Text
                  numberOfLines={1}
                  style={[
                    styles.navigationText,
                    {
                      color: isSelected
                        ? desktopPalette.actionBlue
                        : desktopPalette.primaryText,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 14,
                      fontWeight: isSelected ? "800" : "600",
                    },
                  ]}
                >
                  {item.label}
                </Text>
              </Pressable>
            );
          })}
          <View style={{ flex: 1, minHeight: 12 }} />
          <Pressable
            accessibilityRole="button"
            onPress={onSignOut}
            style={{
              alignItems: "center",
              borderTopColor: desktopPalette.line,
              borderTopWidth: 1,
              flexDirection: "row",
              gap: designTokens.spacing.md,
              marginTop: 6,
              minHeight: 46,
              paddingHorizontal: 14,
            }}
          >
            <Text
              style={[
                styles.navigationText,
                {
                  color: desktopPalette.mutedText,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 13,
                  fontWeight: "600",
                },
              ]}
            >
              Sign out
            </Text>
          </Pressable>
        </View>

        {/* Main content column */}
        <View
          onLayout={(event) => onOnboardingRegionLayout("main", event)}
          style={[
            styles.patientDesktopBody,
            {
              backgroundColor: isDesktopAgentPage
                ? desktopPalette.agentBlue
                : desktopPalette.body,
              flex: 1,
              minWidth: 0,
            },
          ]}
        >
          {statusText ? (
            <Text
              style={[
                styles.metricLabel,
                {
                  color: hasPatientWorkspaceError
                    ? theme.criticalText
                    : desktopPalette.mutedText,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 13,
                  textAlign: "center",
                },
              ]}
            >
              {statusText}
            </Text>
          ) : null}

          {isDesktopAgentPage
            ? renderPatientWorkspaceDesktopAgentSurface({
              conversationMessages,
              contentMaxWidth: desktopContentMaxWidth,
              draftValue,
              isMessageCommandSubmitting,
              layout,
                navItems: desktopMenuItems,
                noticeText,
                onConversationContentSizeChange,
                onDraftChange,
                onNavigateTab: setSelectedPatientWorkspaceTabId,
                onNoticeChange,
                onOpenSupportModal,
                readAloudControls,
                onSendMessage,
                patientConversationScrollRef,
                palette: desktopPalette,
                summary,
                voiceInputControls,
                viewportHeight: desktopViewportHeight,
              })
            : selectedPatientWorkspaceTabId === "overview"
                ? renderPatientWorkspaceDesktopHome({
                    contentMaxWidth: desktopContentMaxWidth,
                    layout,
                    onDownloadDocument,
                    onNavigateTab: setSelectedPatientWorkspaceTabId,
                    onOpenCareDetailModal,
                    palette: desktopPalette,
                    summary,
                  })
              : renderPatientWorkspaceDesktopTabSurface({
                  contentMaxWidth: desktopContentMaxWidth,
                  isUploadSubmitting: isDocumentUploadSubmitting,
                  layout,
                  noticeText,
                  onDownloadDocument,
                  onUploadDocument,
                  onOpenCareDetailModal,
                  onNavigateTab: setSelectedPatientWorkspaceTabId,
                  onUpdateReminderTime,
                  palette: desktopPalette,
                  selectedPatientWorkspaceTabId,
                  selectedTitle:
                    selectedPatientWorkspaceTabId === "settings"
                      ? "Profile"
                      : selectedMenuItem?.label ?? "Patient workspace",
                  summary,
                  tabContent,
                })}
        </View>

        {/* Direction 2 — floating assistant button */}
        {isDesktopAgentPage || !canOpenMessages ? null : (
          <Pressable
            accessibilityLabel={`Ask ${brand.assistantName}`}
            accessibilityRole="button"
            onLayout={(event) => onOnboardingRegionLayout("agent", event)}
            onPress={() => setSelectedPatientWorkspaceTabId("messages")}
            style={[
              createWebGradientBackgroundStyle(desktopPalette.shellGradient),
              {
                alignItems: "center",
                backgroundColor: desktopPalette.actionBlue,
                borderRadius: designTokens.radius.pill,
                bottom: 24,
                flexDirection: "row",
                gap: designTokens.spacing.sm,
                paddingHorizontal: 22,
                paddingVertical: 14,
                position: "absolute",
                right: 24,
                shadowColor: desktopPalette.darkBlue,
                shadowOffset: { height: 8, width: 0 },
                shadowOpacity: 0.3,
                shadowRadius: 20,
              },
            ]}
          >
            <Text
              style={[
                styles.navigationText,
                {
                  color: desktopPalette.white,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 14,
                  fontWeight: "800",
                },
              ]}
            >
              {brand.assistantName}
            </Text>
          </Pressable>
        )}
      </View>

      {renderPatientDesktopFooter({
        layout,
        onOpenSupportModal,
        palette: desktopPalette,
        viewportWidth,
      })}
      {renderPatientDesktopCareDetailModal({
        layout,
        modalState: patientDesktopCareDetailModal,
        onClose: onCloseCareDetailModal,
        onNavigateMessages: () => setSelectedPatientWorkspaceTabId("messages"),
        onNoticeChange,
        onSendMessage,
        palette: desktopPalette,
      })}
      {supportModalElement}
      {renderPatientLegacyOnboardingOverlay({
        flow: onboardingFlow,
        isCommandSubmitting: isOnboardingCommandSubmitting,
        isMediumLayout: true,
        layout,
        layoutRects,
        onBack: onOnboardingBack,
        onNext: onOnboardingNext,
        onSkip: onOnboardingSkip,
        theme,
        tokens,
      })}
    </View>
  );
}

export function renderPatientWorkspaceDesktopAuthScreen(input: {
  authNotice: string | null;
  authStep: "phone" | "code";
  layout: typeof designTokens.semanticLayout;
  legacyHospitalLogoSource: { uri: string };
  onChangeOtpCode: (value: string) => void;
  onChangePhoneNumber: (value: string) => void;
  onRequestCode: () => void;
  onVerifyCode: () => void;
  otpCode: string;
  phoneNumber: string;
  theme: AppShellTheme;
  tokens: typeof designTokens;
  viewportHeight: number;
  viewportWidth: number;
}) {
  const {
    authNotice,
    authStep,
    layout,
    legacyHospitalLogoSource,
    onChangeOtpCode,
    onChangePhoneNumber,
    onRequestCode,
    onVerifyCode,
    otpCode,
    phoneNumber,
    theme,
    tokens,
    viewportHeight,
    viewportWidth,
  } = input;
  const desktopViewportHeight = Math.max(720, viewportHeight);
  const desktopHorizontalPadding =
    viewportWidth < 960 ? 24 : viewportWidth < 1180 ? 40 : 72;
  const desktopContentMaxWidth = Math.max(
    320,
    Math.min(1280, viewportWidth - desktopHorizontalPadding * 2),
  );
  const desktopHeaderMaxWidth = Math.max(
    320,
    Math.min(1600, viewportWidth - desktopHorizontalPadding * 2),
  );
  const isCodeStep = authStep === "code";

  return (
    <View
      style={[
        styles.patientDesktopRoot,
        {
          backgroundColor: patientDesktopPalette.body,
          height: desktopViewportHeight,
          minHeight: desktopViewportHeight,
        },
      ]}
    >
      <View
        style={[
          styles.patientDesktopTopBar,
          createWebGradientBackgroundStyle(patientDesktopPalette.shellGradient),
          {
            backgroundColor: patientDesktopPalette.darkBlue,
            minHeight: 54,
          },
        ]}
      >
        <View
          style={[
            styles.patientDesktopTopBarInner,
            {
              maxWidth: desktopHeaderMaxWidth,
            },
          ]}
        >
          <View
            style={[
              styles.brandLockupRow,
              {
                gap: designTokens.spacing.sm,
              },
            ]}
          >
            <Text
              style={[
                styles.navigationText,
                {
                  color: patientDesktopPalette.white,
                  fontFamily: layout.displayFontFamily,
                  fontSize: 16,
                  fontWeight: "700",
                },
              ]}
            >
              {brand.brandName}
            </Text>
          </View>
        </View>
      </View>

      <View
        style={[
          styles.patientDesktopHero,
          {
            backgroundColor: patientDesktopPalette.lightBlue,
            flex: 1,
          },
        ]}
      >
        <View
          style={[
            styles.patientDesktopHeroInner,
            {
              alignItems: "center",
              justifyContent: "center",
              maxWidth: desktopContentMaxWidth,
              paddingVertical: designTokens.spacing.xxl,
            },
          ]}
        >
          <View
            style={{
              alignItems: "center",
              backgroundColor: patientDesktopPalette.white,
              borderColor: patientDesktopPalette.line,
              borderRadius: designTokens.radius.lg,
              borderWidth: 1,
              gap: designTokens.spacing.lg,
              maxWidth: 560,
              paddingHorizontal: designTokens.spacing.xxl,
              paddingVertical: 36,
              width: "100%",
            }}
          >
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: patientDesktopPalette.primaryText,
                  fontFamily: layout.displayFontFamily,
                  fontSize: tokens.typography.fontSize["3xl"],
                  lineHeight: tokens.typography.lineHeight["3xl"],
                  textAlign: "center",
                },
              ]}
            >
              {`Welcome to ${brand.brandName}!`}
            </Text>
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: patientDesktopPalette.primaryText,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.md,
                  fontWeight: "700",
                  lineHeight: tokens.typography.lineHeight.md,
                  maxWidth: 420,
                  textAlign: "center",
                },
              ]}
            >
              {isCodeStep
                ? "Please enter the 6-digit code sent to your phone."
                : "Please enter your phone number to continue."}
            </Text>

            <View
              style={{
                gap: designTokens.spacing.md,
                maxWidth: 420,
                width: "100%",
              }}
            >
              <TextInput
                keyboardType={isCodeStep ? "number-pad" : "phone-pad"}
                maxLength={isCodeStep ? 8 : 24}
                onChangeText={
                  isCodeStep ? onChangeOtpCode : onChangePhoneNumber
                }
                onSubmitEditing={isCodeStep ? onVerifyCode : onRequestCode}
                placeholder={
                  isCodeStep ? "Enter verification code" : "Phone number"
                }
                placeholderTextColor={patientDesktopPalette.mutedText}
                style={[
                  styles.composerInput,
                  {
                    backgroundColor: patientDesktopPalette.white,
                    borderColor: patientDesktopPalette.inputBorder,
                    borderRadius: designTokens.radius.md,
                    borderWidth: 1,
                    color: patientDesktopPalette.primaryText,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.md,
                    lineHeight: tokens.typography.lineHeight.md,
                    paddingHorizontal: designTokens.spacing.lg,
                    paddingVertical: 14,
                  },
                ]}
                value={isCodeStep ? otpCode : phoneNumber}
              />
              <Pressable
                onPress={isCodeStep ? onVerifyCode : onRequestCode}
                style={{
                  alignItems: "center",
                  backgroundColor: patientDesktopPalette.linkBlue,
                  borderColor: patientDesktopPalette.linkBlue,
                  borderRadius: designTokens.radius.md,
                  borderWidth: 1,
                  minHeight: 48,
                  justifyContent: "center",
                }}
              >
                <Text
                  style={[
                    styles.navigationText,
                    {
                      color: patientDesktopPalette.white,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.md,
                      fontWeight: "700",
                    },
                  ]}
                >
                  Continue
                </Text>
              </Pressable>
            </View>

            {isCodeStep ? (
              <Pressable onPress={onRequestCode}>
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: patientDesktopPalette.linkBlue,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.sm,
                      fontWeight: "700",
                      textAlign: "center",
                    },
                  ]}
                >
                  Request a new code
                </Text>
              </Pressable>
            ) : null}

            {authNotice ? (
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: theme.info,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                    lineHeight: tokens.typography.lineHeight.md,
                    maxWidth: 420,
                    textAlign: "center",
                  },
                ]}
              >
                {authNotice}
              </Text>
            ) : null}
          </View>
        </View>
      </View>

      {renderPatientDesktopFooter({
        layout,
        onOpenSupportModal: () => {},
        palette: patientDesktopPalette,
        viewportWidth,
      })}
    </View>
  );
}

function renderPatientDesktopMicrophoneGlyph(
  color: string,
  isListening: boolean,
) {
  return (
    <LineIcon
      color={color}
      name={isListening ? "stop" : "microphone"}
      size={24}
      strokeWidth={2.4}
    />
  );
}

function renderPatientDesktopFeedbackGlyph(input: {
  color: string;
  direction: "up" | "down";
}) {
  const { color, direction } = input;

  return (
    <View
      style={{
        alignItems: "center",
        height: 18,
        justifyContent: "center",
        transform: [{ rotate: direction === "down" ? "180deg" : "0deg" }],
        width: 18,
      }}
    >
      <LineIcon color={color} name="thumb-up" size={18} strokeWidth={2.3} />
    </View>
  );
}

function renderPatientDesktopToggle(isOn: boolean, color: string) {
  return (
    <View
      style={[
        styles.patientDesktopToggle,
        {
          alignItems: isOn ? "flex-end" : "flex-start",
          backgroundColor: isOn ? color : "transparent",
          borderColor: color,
          borderRadius: designTokens.radius.md,
          justifyContent: "center",
          paddingHorizontal: designTokens.spacing.xxs,
        },
      ]}
    >
      <View
        style={{
          backgroundColor: isOn ? patientMobilePalette.white : color,
          borderRadius: 5,
          height: 8,
          width: 8,
        }}
      />
    </View>
  );
}

function renderPatientDesktopSpeakerGlyph(input: {
  color: string;
  isActive: boolean;
}) {
  const { color, isActive } = input;

  return (
    <LineIcon
      color={color}
      name={isActive ? "volume" : "volume-off"}
      size={25}
      strokeWidth={2.4}
    />
  );
}

function renderPatientDesktopStopGlyph(color: string) {
  return <LineIcon color={color} name="stop" size={18} strokeWidth={2.3} />;
}

function renderPatientDesktopTileIcon(itemId: PatientWorkspaceTabId) {
  const iconColor = patientMobilePalette.actionBlue;

  if (itemId === "appointments") {
    return (
      <View
        style={{
          borderColor: iconColor,
          borderRadius: 2,
          borderWidth: 2,
          height: 16,
          justifyContent: "flex-end",
          padding: designTokens.spacing.xxs,
          width: 16,
        }}
      >
        <View
          style={{
            backgroundColor: iconColor,
            height: 3,
            width: "100%",
          }}
        />
      </View>
    );
  }

  if (itemId === "procedures") {
    return (
      <View
        style={{
          alignItems: "center",
          height: 18,
          justifyContent: "center",
          width: 18,
        }}
      >
        <View
          style={{
            backgroundColor: iconColor,
            height: 12,
            width: 3,
          }}
        />
        <View
          style={{
            backgroundColor: iconColor,
            height: 3,
            marginTop: 1,
            width: 14,
          }}
        />
        <View
          style={{
            borderColor: iconColor,
            borderRadius: designTokens.radius.sm,
            borderWidth: 2,
            height: 8,
            position: "absolute",
            top: 0,
            width: 8,
          }}
        />
      </View>
    );
  }

  if (itemId === "medications") {
    return (
      <View
        style={{
          backgroundColor: iconColor,
          borderRadius: 2,
          height: 14,
          marginTop: 3,
          width: 17,
        }}
      >
        <View
          style={{
            borderColor: iconColor,
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            borderWidth: 2,
            height: 8,
            left: 4,
            position: "absolute",
            top: -6,
            width: 9,
          }}
        />
      </View>
    );
  }

  if (itemId === "settings") {
    return (
      <View
        style={{
          alignItems: "center",
          borderColor: iconColor,
          borderRadius: designTokens.radius.md,
          borderWidth: 2,
          height: 18,
          justifyContent: "center",
          width: 18,
        }}
      >
        <View
          style={{
            backgroundColor: iconColor,
            borderRadius: designTokens.radius.sm,
            height: 6,
            width: 6,
          }}
        />
      </View>
    );
  }

  if (itemId === "calls") {
    return (
      <View
        style={{
          alignItems: "center",
          borderColor: iconColor,
          borderRadius: 9,
          borderWidth: 2,
          height: 18,
          justifyContent: "center",
          width: 18,
        }}
      >
        <View
          style={{
            backgroundColor: iconColor,
            borderRadius: 3,
            height: 4,
            transform: [{ rotate: "-25deg" }],
            width: 12,
          }}
        />
      </View>
    );
  }

  return (
    <View
      style={{
        borderColor: iconColor,
        borderRadius: 2,
        borderWidth: 2,
        height: 18,
        paddingHorizontal: 3,
        paddingTop: designTokens.spacing.xs,
        width: 15,
      }}
    >
      <View style={{ backgroundColor: iconColor, height: 2, width: "100%" }} />
      <View
        style={{
          backgroundColor: iconColor,
          height: 2,
          marginTop: 3,
          width: "75%",
        }}
      />
    </View>
  );
}

function formatPatientDesktopDateGroup(date: Date): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const target = new Date(date);
  target.setHours(0, 0, 0, 0);

  const dayDelta = Math.round(
    (target.getTime() - today.getTime()) / 86_400_000,
  );

  if (dayDelta === 0) {
    return "Today";
  }

  if (dayDelta === 1) {
    return "Tomorrow";
  }

  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "long",
    weekday: "long",
  }).format(date);
}

function formatPatientDesktopTime(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    hour12: true,
    minute: "2-digit",
  }).format(date);
}

function buildPatientDesktopCalendarRows(
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null,
) {
  const appointmentRows = sortPatientDesktopDetailRowsForCareSurface(
    readPatientWorkspaceDetailRows(summary, "appointments"),
    "appointments",
  );
  const rows =
    appointmentRows.length > 0
      ? appointmentRows
      : summary?.nextAppointmentText
        ? [
            {
              id: "next-appointment",
              label: "Upcoming visit",
              valueText: summary.nextAppointmentText,
            },
          ]
        : [];

  return rows.slice(0, 3).map((row, index) => {
    const sourceParts = readPatientDesktopSourceParts(row.valueText);
    const dateText =
      sourceParts.find((part) => parseWorkbenchDateTimeText(part)) ?? "";
    const parsedDate = parseWorkbenchDateTimeText(dateText);

    return {
      dateText:
        parsedDate != null
          ? formatPatientDesktopDateCell(parsedDate).primaryText
          : "Next",
      detailText:
        parsedDate != null
          ? [summary?.programLabel, summary?.coordinatorLabel]
              .filter((part) => part != null && part.trim().length > 0)
              .join(" - ") || row.valueText
          : row.valueText || "Appointment details unavailable",
      groupText:
        parsedDate != null ? formatPatientDesktopDateGroup(parsedDate) : "Next",
      id: row.id || `appointment-${index}`,
      timeText:
        parsedDate != null
          ? formatPatientDesktopTime(parsedDate)
          : row.valueText || "Time pending",
      titleText: row.label || "Upcoming visit",
    };
  });
}

function buildPatientDesktopMedicationRows(
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null,
) {
  const medicationRows = sortPatientDesktopDetailRowsForCareSurface(
    readPatientWorkspaceDetailRows(summary, "medications"),
    "medications",
  );
  const routines = ["morning", "midday", "evening"];

  if (medicationRows.length > 0) {
    return medicationRows.map((row, index) => {
      const recordState = resolvePatientDesktopClinicalRecordState(
        row,
        "medications",
      );

      return {
        detailText:
          row.notes?.[0]?.bodyText ??
          row.valueText ??
          "Medication detail unavailable",
        id: row.id || `medication-${index}`,
        isHistorical: recordState.isHistorical,
        routineText: recordState.isHistorical
          ? "history"
          : routines[index] ?? "today",
        statusText: recordState.labelText,
        titleText: row.label || "Medication note",
      };
    });
  }

  if (summary?.medicationSummaryText?.trim()) {
    return [
      {
        detailText: summary.medicationSummaryText,
        id: "medication-summary",
        isHistorical: false,
        routineText: "today",
        statusText: summary?.statusLabel ?? "Current",
        titleText: "Medication note",
      },
    ];
  }

  return [
    {
      detailText:
        "Medication guidance will appear here when it is returned by the source.",
      id: "medication-empty",
      isHistorical: false,
      routineText: "today",
      statusText: "Pending",
      titleText: "Medication guidance pending",
    },
  ];
}

function resolvePatientDesktopReminderOption(
  routineText: string,
  index: number,
): PatientMedicationReminderTimeUpdateInput {
  const normalizedRoutine = routineText.trim().toLowerCase();
  const matchingOption = patientDesktopMedicationReminderOptions.find(
    (option) => option.slotId === normalizedRoutine,
  );

  return (
    matchingOption ??
    patientDesktopMedicationReminderOptions[
      Math.min(index, patientDesktopMedicationReminderOptions.length - 1)
    ] ??
    patientDesktopMedicationReminderOptions[0]
  );
}

function formatPatientDesktopOrdinalDay(day: number): string {
  const suffix =
    day % 100 >= 11 && day % 100 <= 13
      ? "th"
      : day % 10 === 1
        ? "st"
        : day % 10 === 2
          ? "nd"
          : day % 10 === 3
            ? "rd"
            : "th";

  return `${day}${suffix}`;
}

function formatPatientDesktopDateCell(date: Date): PatientDesktopTableCell {
  const month = new Intl.DateTimeFormat("en-US", { month: "short" }).format(
    date,
  );

  return {
    primaryText: `${month} ${formatPatientDesktopOrdinalDay(date.getDate())}`,
    secondaryText: formatPatientDesktopDateGroup(date),
  };
}

function parsePatientDesktopDatedValue(valueText: string): {
  dateCell: PatientDesktopTableCell;
  timeCell: PatientDesktopTableCell;
} {
  const sourceDateText = valueText.split("|")[0]?.trim() ?? "";
  const parsedDate = parseWorkbenchDateTimeText(sourceDateText);

  if (!parsedDate) {
    return {
      dateCell: {
        primaryText: sourceDateText || "Date pending",
        secondaryText: null,
      },
      timeCell: {
        primaryText: "Time pending",
        secondaryText: null,
      },
    };
  }

  return {
    dateCell: {
      ...formatPatientDesktopDateCell(parsedDate),
      secondaryText: [
        formatPatientDesktopDateGroup(parsedDate),
        formatPatientDesktopTime(parsedDate),
      ].join(" - "),
    },
    timeCell: {
      primaryText: formatPatientDesktopTime(parsedDate),
    },
  };
}

function buildPatientDesktopAppointmentTableRows(
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null,
  onOpenDetail: (detail: NonNullable<PatientDesktopCareDetailModalState>) => void,
): PatientDesktopTableRow[] {
  const sourceRows = sortPatientDesktopDetailRowsForCareSurface(
    readPatientWorkspaceDetailRows(summary, "appointments"),
    "appointments",
  );
  const rows =
    sourceRows.length > 0
      ? sourceRows
      : summary?.nextAppointmentText
        ? [
            {
              id: "next-appointment",
              label: "Upcoming visit",
              valueText: summary.nextAppointmentText,
            },
          ]
        : [];

  return rows.map((row, index) => {
    const detailModal = buildPatientDesktopAppointmentDetailModal({
      index,
      row,
      summary,
    });
    const sourceParts = readPatientDesktopSourceParts(row.valueText);
    const { dateCell, timeCell } = parsePatientDesktopDatedValue(row.valueText);
    const locationText =
      readPatientDesktopDetailPart(sourceParts, "Location") ??
      readPatientDesktopDetailPart(sourceParts, "Facility") ??
      readPatientDesktopDetailPart(sourceParts, "Room");
    const statusText = readPatientDesktopDetailPart(sourceParts, "Status");
    const recordState = resolvePatientDesktopClinicalRecordState(
      row,
      "appointments",
    );
    const appointmentName = resolvePatientDesktopSourceName(
      sourceParts,
      row.label || "Upcoming visit",
    );

    return {
      id: row.id || `appointment-row-${index}`,
      cells: [
        dateCell,
        timeCell,
        {
          primaryText: appointmentName,
          secondaryText:
            row.notes?.[0]?.bodyText ??
            [
              recordState.labelText,
              statusText ? `Status: ${statusText}` : null,
            ]
              .filter((part): part is string => part != null)
              .join(" - "),
        },
        {
          primaryText: locationText || "Location not recorded",
          secondaryText: null,
        },
        {
          primaryText:
            summary?.coordinatorLabel || "Receiving clinician not assigned",
          secondaryText: null,
        },
        {
          primaryText: "Details",
          secondaryText: "Review with AI",
          onPress: () => onOpenDetail(detailModal),
          tone: "accent",
        },
      ],
    };
  });
}

function readPatientDesktopDetailPart(
  parts: string[],
  label: string,
): string | null {
  const prefix = `${label}:`;
  const value =
    parts
      .find((part) => part.toLowerCase().startsWith(prefix.toLowerCase()))
      ?.slice(prefix.length)
      .trim() ?? "";

  return value || null;
}

function resolvePatientDesktopSourceName(
  parts: string[],
  fallback: string,
): string {
  return (
    parts.find((part) => {
      if (parseWorkbenchDateTimeText(part)) {
        return false;
      }

      return ![
        "status:",
        "location:",
        "surgeon:",
        "provider:",
        "service:",
        "room:",
        "facility:",
        "complete:",
        "cancel reason:",
      ].some((prefix) => part.toLowerCase().startsWith(prefix));
    }) ??
    fallback
  );
}

function readPatientDesktopSourceParts(valueText: string): string[] {
  return valueText
    .split(/[|\n]+/u)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

function readPatientDesktopLabeledSourcePart(
  part: string,
): { labelText: string; valueText: string } | null {
  const match = part.match(/^([^:]{2,64}):\s*(.+)$/u);

  if (!match) {
    return null;
  }

  const [, rawLabel = "", rawValue = ""] = match;
  const labelText = rawLabel.trim();
  const valueText = rawValue.trim();

  return labelText && valueText ? { labelText, valueText } : null;
}

function appendPatientDesktopDetailRow(
  rows: NonNullable<PatientDesktopCareDetailModalState>["detailRows"][number][],
  row: {
    id: string;
    labelText: string;
    valueText: string | null | undefined;
  },
): void {
  const valueText = row.valueText?.trim() ?? "";

  if (valueText.length === 0) {
    return;
  }

  rows.push({
    id: row.id,
    labelText: row.labelText,
    valueText,
  });
}

function appendPatientDesktopSourceDetailRows(
  rows: NonNullable<PatientDesktopCareDetailModalState>["detailRows"][number][],
  input: {
    excludedLabels: readonly string[];
    parts: readonly string[];
    rowId: string;
  },
): void {
  const excludedLabels = new Set(
    input.excludedLabels.map((label) => label.trim().toLowerCase()),
  );

  input.parts.forEach((part, index) => {
    const labeledPart = readPatientDesktopLabeledSourcePart(part);

    if (!labeledPart) {
      return;
    }

    if (excludedLabels.has(labeledPart.labelText.toLowerCase())) {
      return;
    }

    appendPatientDesktopDetailRow(rows, {
      id: `${input.rowId}-source-${index}`,
      labelText: labeledPart.labelText,
      valueText: labeledPart.valueText,
    });
  });
}

function appendPatientDesktopClinicalNoteRows(
  rows: NonNullable<PatientDesktopCareDetailModalState>["detailRows"][number][],
  sourceRow: PatientWorkspaceDetailRow,
): void {
  for (const note of sourceRow.notes ?? []) {
    appendPatientDesktopDetailRow(rows, {
      id: `${sourceRow.id}-${note.noteId}`,
      labelText: note.title || "Clinical note",
      valueText: note.bodyText,
    });
  }
}

function formatPatientDesktopSourceDateTime(valueText: string): string | null {
  const datePart =
    readPatientDesktopSourceParts(valueText).find((part) =>
      Boolean(parseWorkbenchDateTimeText(part)),
    ) ?? null;
  const parsedDate = datePart ? parseWorkbenchDateTimeText(datePart) : null;

  if (!parsedDate) {
    return datePart;
  }

  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    hour: "numeric",
    hour12: true,
    minute: "2-digit",
    month: "short",
    year: "numeric",
  }).format(parsedDate);
}

function buildPatientDesktopCareDetailPrompt(input: {
  detailRows: NonNullable<PatientDesktopCareDetailModalState>["detailRows"];
  kind: PatientDesktopCareDetailKind;
  titleText: string;
}): string {
  const detailText = input.detailRows
    .map((row) => `${row.labelText}: ${row.valueText}`)
    .join("; ");

  return [
    `Please review this ${input.kind} with me: ${input.titleText}.`,
    detailText ? `Details: ${detailText}.` : null,
    "Summarize what matters and tell me what I should ask the care team next.",
  ]
    .filter((part): part is string => typeof part === "string")
    .join(" ");
}

function buildPatientDesktopAppointmentDetailModal(
  input: {
    row: PatientWorkspaceDetailRow;
    summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
    index: number;
  },
): NonNullable<PatientDesktopCareDetailModalState> {
  const sourceParts = readPatientDesktopSourceParts(input.row.valueText);
  const titleText = resolvePatientDesktopSourceName(
    sourceParts,
    input.row.label || `Appointment ${input.index + 1}`,
  );
  const detailRows: NonNullable<PatientDesktopCareDetailModalState>["detailRows"][number][] = [];

  appendPatientDesktopDetailRow(detailRows, {
    id: `${input.row.id}-appointment`,
    labelText: "Appointment",
    valueText: titleText,
  });
  appendPatientDesktopDetailRow(detailRows, {
    id: `${input.row.id}-date-time`,
    labelText: "Date and time",
    valueText: formatPatientDesktopSourceDateTime(input.row.valueText),
  });
  appendPatientDesktopDetailRow(detailRows, {
    id: `${input.row.id}-location`,
    labelText: "Location",
    valueText:
      readPatientDesktopDetailPart(sourceParts, "Location") ??
      readPatientDesktopDetailPart(sourceParts, "Facility") ??
      readPatientDesktopDetailPart(sourceParts, "Room"),
  });
  appendPatientDesktopDetailRow(detailRows, {
    id: `${input.row.id}-status`,
    labelText: "Status",
    valueText: readPatientDesktopDetailPart(sourceParts, "Status"),
  });
  appendPatientDesktopSourceDetailRows(detailRows, {
    excludedLabels: ["date", "time", "location", "facility", "room", "status"],
    parts: sourceParts,
    rowId: input.row.id,
  });
  appendPatientDesktopDetailRow(detailRows, {
    id: `${input.row.id}-record-state`,
    labelText: "Record state",
    valueText: resolvePatientDesktopClinicalRecordState(
      input.row,
      "appointments",
    ).labelText,
  });
  appendPatientDesktopDetailRow(detailRows, {
    id: `${input.row.id}-clinician`,
    labelText: "Receiving clinician",
    valueText: input.summary?.coordinatorLabel,
  });
  appendPatientDesktopClinicalNoteRows(detailRows, input.row);

  return {
    badgeText: "VIS",
    detailRows,
    itemId: input.row.id || `appointment-${input.index}`,
    kind: "appointment",
    reviewPromptText: buildPatientDesktopCareDetailPrompt({
      detailRows,
      kind: "appointment",
      titleText,
    }),
    subtitleText: detailRows.find((row) => row.labelText === "Date and time")?.valueText ?? null,
    titleText,
  };
}

function buildPatientDesktopProcedureDetailModal(
  input: {
    row: PatientWorkspaceDetailRow;
    summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
    index: number;
  },
): NonNullable<PatientDesktopCareDetailModalState> {
  const sourceParts = readPatientDesktopSourceParts(input.row.valueText);
  const titleText = resolvePatientDesktopProcedureName(
    sourceParts,
    input.row.label || `Procedure ${input.index + 1}`,
  );
  const detailRows: NonNullable<PatientDesktopCareDetailModalState>["detailRows"][number][] = [];

  appendPatientDesktopDetailRow(detailRows, {
    id: `${input.row.id}-procedure`,
    labelText: "Procedure",
    valueText: titleText,
  });
  appendPatientDesktopDetailRow(detailRows, {
    id: `${input.row.id}-date-time`,
    labelText: "Date and time",
    valueText: formatPatientDesktopSourceDateTime(input.row.valueText),
  });
  appendPatientDesktopDetailRow(detailRows, {
    id: `${input.row.id}-location`,
    labelText: "Location",
    valueText:
      readPatientDesktopDetailPart(sourceParts, "Location") ??
      readPatientDesktopDetailPart(sourceParts, "Facility") ??
      readPatientDesktopDetailPart(sourceParts, "Room"),
  });
  appendPatientDesktopDetailRow(detailRows, {
    id: `${input.row.id}-status`,
    labelText: "Status",
    valueText: readPatientDesktopDetailPart(sourceParts, "Status"),
  });
  appendPatientDesktopSourceDetailRows(detailRows, {
    excludedLabels: ["date", "time", "location", "facility", "room", "status"],
    parts: sourceParts,
    rowId: input.row.id,
  });
  appendPatientDesktopDetailRow(detailRows, {
    id: `${input.row.id}-record-state`,
    labelText: "Record state",
    valueText: resolvePatientDesktopClinicalRecordState(
      input.row,
      "procedures",
    ).labelText,
  });
  appendPatientDesktopDetailRow(detailRows, {
    id: `${input.row.id}-clinician`,
    labelText: "Leading clinician",
    valueText:
      readPatientDesktopDetailPart(sourceParts, "Surgeon") ??
      readPatientDesktopDetailPart(sourceParts, "Provider") ??
      input.summary?.coordinatorLabel,
  });
  appendPatientDesktopClinicalNoteRows(detailRows, input.row);

  return {
    badgeText: "PROC",
    detailRows,
    itemId: input.row.id || `procedure-${input.index}`,
    kind: "procedure",
    reviewPromptText: buildPatientDesktopCareDetailPrompt({
      detailRows,
      kind: "procedure",
      titleText,
    }),
    subtitleText: detailRows.find((row) => row.labelText === "Date and time")?.valueText ?? null,
    titleText,
  };
}

function buildPatientDesktopMedicationDetailModal(
  input: {
    row: PatientWorkspaceDetailRow | null;
    summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
    index: number;
    titleText: string;
    detailText: string;
    statusText: string;
  },
): NonNullable<PatientDesktopCareDetailModalState> {
  const detailRows: NonNullable<PatientDesktopCareDetailModalState>["detailRows"][number][] = [];
  const rowId = input.row?.id ?? `medication-${input.index}`;
  const sourceParts = readPatientDesktopSourceParts(input.row?.valueText ?? "");

  appendPatientDesktopDetailRow(detailRows, {
    id: `${rowId}-medication`,
    labelText: "Medication",
    valueText: input.titleText,
  });
  appendPatientDesktopSourceDetailRows(detailRows, {
    excludedLabels: [],
    parts: sourceParts,
    rowId,
  });
  appendPatientDesktopDetailRow(detailRows, {
    id: `${rowId}-instructions`,
    labelText: "Instructions",
    valueText: input.detailText,
  });
  appendPatientDesktopDetailRow(detailRows, {
    id: `${rowId}-status`,
    labelText: "Status",
    valueText: input.statusText,
  });
  appendPatientDesktopDetailRow(detailRows, {
    id: `${rowId}-clinician`,
    labelText: "Care team",
    valueText: input.summary?.coordinatorLabel,
  });

  if (input.row) {
    appendPatientDesktopClinicalNoteRows(detailRows, input.row);
  }

  return {
    badgeText: "RX",
    detailRows,
    itemId: rowId,
    kind: "medication",
    reviewPromptText: buildPatientDesktopCareDetailPrompt({
      detailRows,
      kind: "medication",
      titleText: input.titleText,
    }),
    subtitleText: input.detailText,
    titleText: input.titleText,
  };
}

function buildPatientDesktopDocumentDetailModal(
  document: PatientWorkspaceDocumentSummary,
): NonNullable<PatientDesktopCareDetailModalState> {
  const detailRows: NonNullable<PatientDesktopCareDetailModalState>["detailRows"][number][] = [];

  appendPatientDesktopDetailRow(detailRows, {
    id: `${document.documentId}-status`,
    labelText: "Status",
    valueText: document.statusLabel,
  });
  appendPatientDesktopDetailRow(detailRows, {
    id: `${document.documentId}-availability`,
    labelText: "Availability",
    valueText:
      document.availability === "download-ready"
        ? "Download ready"
        : "Metadata only",
  });
  appendPatientDesktopDetailRow(detailRows, {
    id: `${document.documentId}-source`,
    labelText: "Source",
    valueText: document.createdByLabel,
  });
  appendPatientDesktopDetailRow(detailRows, {
    id: `${document.documentId}-created`,
    labelText: "Created",
    valueText: document.createdAt.slice(0, 16).replace("T", " "),
  });
  appendPatientDesktopDetailRow(detailRows, {
    id: `${document.documentId}-summary`,
    labelText: "Summary",
    valueText: document.summaryText,
  });
  for (const note of document.notes ?? []) {
    appendPatientDesktopDetailRow(detailRows, {
      id: `${document.documentId}-${note.noteId}`,
      labelText: note.title || "Clinical note",
      valueText: note.bodyText,
    });
  }

  return {
    badgeText: "DOC",
    detailRows,
    itemId: document.documentId,
    kind: "document",
    reviewPromptText: buildPatientDesktopCareDetailPrompt({
      detailRows,
      kind: "document",
      titleText: document.label,
    }),
    subtitleText: document.summaryText,
    titleText: document.label,
  };
}

function resolvePatientDesktopProcedureName(
  parts: string[],
  fallback: string,
): string {
  return resolvePatientDesktopSourceName(parts, fallback);
}

function buildPatientDesktopProcedureTableRows(
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null,
  onOpenDetail: (detail: NonNullable<PatientDesktopCareDetailModalState>) => void,
): PatientDesktopTableRow[] {
  const sourceRows = sortPatientDesktopDetailRowsForCareSurface(
    readPatientWorkspaceDetailRows(summary, "procedures"),
    "procedures",
  );
  const rows =
    sourceRows.length > 0
      ? sourceRows
      : summary?.procedureSummaryText
        ? [
            {
              id: "procedure-summary",
              label: "Upcoming procedure",
              valueText: summary.procedureSummaryText,
            },
          ]
        : [];

  return rows.map((row, index) => {
    const detailModal = buildPatientDesktopProcedureDetailModal({
      index,
      row,
      summary,
    });
    const sourceParts = readPatientDesktopSourceParts(row.valueText);
    const { dateCell, timeCell } = parsePatientDesktopDatedValue(row.valueText);
    const locationText =
      readPatientDesktopDetailPart(sourceParts, "Location") ??
      readPatientDesktopDetailPart(sourceParts, "Facility") ??
      readPatientDesktopDetailPart(sourceParts, "Room");
    const surgeonText =
      readPatientDesktopDetailPart(sourceParts, "Surgeon") ??
      readPatientDesktopDetailPart(sourceParts, "Provider");
    const statusText = readPatientDesktopDetailPart(sourceParts, "Status");
    const recordState = resolvePatientDesktopClinicalRecordState(
      row,
      "procedures",
    );

    return {
      id: row.id || `procedure-row-${index}`,
      cells: [
        dateCell,
        timeCell,
        {
          primaryText: resolvePatientDesktopProcedureName(
            sourceParts,
            row.label || "Upcoming procedure",
          ),
          secondaryText:
            row.notes?.[0]?.bodyText ??
            [
              recordState.labelText,
              statusText ? `Status: ${statusText}` : null,
            ]
              .filter((part): part is string => part != null)
              .join(" - "),
        },
        {
          primaryText: locationText || summary?.programLabel || "Care plan",
          secondaryText: summary?.statusLabel || null,
        },
        {
          primaryText: surgeonText || summary?.coordinatorLabel || "Care team",
          secondaryText: summary?.contactWindowText || null,
        },
        {
          primaryText: "Details",
          secondaryText: "Review with AI",
          onPress: () => onOpenDetail(detailModal),
          tone: "accent",
        },
      ],
    };
  });
}

function buildPatientDesktopMedicationTableRows(
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null,
  onOpenDetail: (detail: NonNullable<PatientDesktopCareDetailModalState>) => void,
  onUpdateReminderTime: (
    input: PatientMedicationReminderTimeUpdateInput,
  ) => void,
): PatientDesktopTableRow[] {
  const preferredWakeUpTime =
    summary?.onboarding.preferences.remindersEnabled === false
      ? null
      : summary?.onboarding.preferences.wakeUpTime;

  const sourceRows = sortPatientDesktopDetailRowsForCareSurface(
    readPatientWorkspaceDetailRows(summary, "medications"),
    "medications",
  );

  return buildPatientDesktopMedicationRows(summary).map((row, index) => {
    const sourceRow = sourceRows[index] ?? null;
    const detailModal = buildPatientDesktopMedicationDetailModal({
      detailText: row.detailText,
      index,
      row: sourceRow,
      summary,
      statusText: row.statusText,
      titleText: row.titleText,
    });

    return {
      id: row.id,
      cells: [
        {
          primaryText: row.routineText,
        },
        {
          primaryText: row.titleText,
          secondaryText: "Medication",
        },
        {
          primaryText: row.isHistorical ? row.statusText : "1pc",
          secondaryText: row.isHistorical ? "Status" : "Dosage",
        },
        {
          primaryText: row.detailText,
          secondaryText: null,
        },
        {
          primaryText: row.isHistorical ? "History" : "REMIND ME",
          secondaryText: null,
          onPress: row.isHistorical
            ? undefined
            : () => {
                const fallbackOption = resolvePatientDesktopReminderOption(
                  row.routineText,
                  index,
                );
                const wakeUpTime =
                  preferredWakeUpTime ?? fallbackOption.wakeUpTime;

                onUpdateReminderTime({
                  ...fallbackOption,
                  displayTime: formatPatientMobilePreferenceTime(wakeUpTime),
                  wakeUpTime,
                });
              },
          tone: row.isHistorical ? null : "accent",
        },
        {
          primaryText: "Details",
          secondaryText: "Review with AI",
          onPress: () => onOpenDetail(detailModal),
          tone: "accent",
        },
      ],
    };
  });
}

function buildPatientDesktopDocumentTableRows(
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null,
  onOpenDetail?: (detail: NonNullable<PatientDesktopCareDetailModalState>) => void,
  onDownloadDocument?: (document: PatientWorkspaceDocumentSummary) => void,
): PatientDesktopTableRow[] {
  const documents = summary?.documentSummaries ?? [];

  if (documents.length === 0) {
    return [
      {
        id: "documents-empty",
        cells: [
          {
            primaryText: "No date",
            secondaryText: null,
          },
          {
            primaryText: "No patient documents ready",
            secondaryText: "Records will appear here when they are available.",
          },
          {
            primaryText: "Pending",
          },
          {
            primaryText: "Metadata",
          },
          {
            primaryText: "Ask care team",
            onPress: undefined,
            tone: "accent",
          },
        ],
      },
    ];
  }

  return documents.map((document) => {
    const createdDate = new Date(document.createdAt);
    const createdAtText = Number.isNaN(createdDate.getTime())
      ? document.createdAt.slice(0, 10)
      : new Intl.DateTimeFormat("en-US", {
          day: "numeric",
          month: "short",
          year: "numeric",
        }).format(createdDate);
    const createdTimeText = Number.isNaN(createdDate.getTime())
      ? null
      : new Intl.DateTimeFormat("en-US", {
          hour: "numeric",
          hour12: true,
          minute: "2-digit",
        }).format(createdDate);

    return {
      id: document.documentId,
      cells: [
        {
          primaryText: createdAtText,
          secondaryText: createdTimeText,
        },
        {
          primaryText: document.label,
          secondaryText: document.notes?.[0]?.bodyText ?? document.summaryText,
        },
        {
          primaryText: resolvePatientDocumentType(document.label),
        },
        {
          primaryText: document.createdByLabel,
          secondaryText:
            document.availability === "download-ready"
              ? "Download ready"
              : document.statusLabel,
        },
        {
          primaryText: "Details",
          secondaryText:
            document.availability === "download-ready"
              ? "Download ready"
              : "Review with AI",
          actions: [
            ...(document.availability === "download-ready" && onDownloadDocument
              ? [
                  {
                    labelText: "Download",
                    onPress: () => onDownloadDocument(document),
                    tone: "success" as const,
                  },
                ]
              : []),
            ...(onOpenDetail
              ? [
                  {
                    labelText: "Details",
                    onPress: () =>
                      onOpenDetail(
                        buildPatientDesktopDocumentDetailModal(document),
                      ),
                    tone: "accent" as const,
                  },
                ]
              : []),
          ],
          onPress: onOpenDetail
            ? () => onOpenDetail(buildPatientDesktopDocumentDetailModal(document))
            : undefined,
          tone: "accent",
        },
      ],
    };
  });
}

function renderPatientDesktopTable(input: {
  columns: readonly PatientDesktopTableColumn[];
  layout: typeof designTokens.semanticLayout;
  palette: PatientDesktopPalette;
  rows: readonly PatientDesktopTableRow[];
}) {
  const { columns, layout, palette, rows } = input;

  return (
    <View
      style={[
        styles.patientDesktopDataTable,
        {
          backgroundColor: "transparent",
          borderColor: "transparent",
          borderRadius: designTokens.radius.none,
          borderWidth: 0,
          gap: 10,
        },
      ]}
    >
      <View
        style={[
          styles.patientDesktopDataTableHeader,
          {
            backgroundColor: "transparent",
            borderBottomColor: "transparent",
            paddingHorizontal: designTokens.spacing.lg,
            paddingTop: designTokens.spacing.none,
          },
        ]}
      >
        {columns.map((column) => (
          <Text
            key={column.id}
            style={[
              styles.metricLabel,
              {
                color: palette.mutedText,
                flex: column.flex,
                fontFamily: layout.bodyFontFamily,
                fontSize: 10,
                fontWeight: "700",
                letterSpacing: 1,
                textTransform: "uppercase",
              },
            ]}
          >
            {column.labelText}
          </Text>
        ))}
      </View>

      {rows.map((row, rowIndex) => (
        <View
          key={row.id}
          style={[
            styles.patientDesktopDataTableRow,
            {
              backgroundColor: palette.white,
              borderBottomColor: palette.line,
              borderColor: palette.line,
              borderRadius: 14,
              borderWidth: 1,
              paddingHorizontal: 18,
            },
          ]}
        >
          {columns.map((column, columnIndex) => {
            const cell = row.cells[columnIndex] ?? { primaryText: "" };
            const primaryColor =
              cell.tone === "accent"
                ? patientDesktopPalette.strongBlue
                : palette.primaryText;
            const isActionCell = cell.tone === "accent";
            const hasActionButtons = (cell.actions?.length ?? 0) > 0;
            const isPressableAction =
              isActionCell && cell.onPress != null && !hasActionButtons;
            const isEmphasisColumn =
              column.id === "name" || column.id === "procedure";
            const isLeadColumn = column.id === "date" || column.id === "time";
            const actionButtons =
              hasActionButtons && cell.actions ? (
                <View style={[styles.patientRowHeader, { gap: 6 }]}>
                  {cell.actions.map((action) => {
                    const actionColor =
                      action.tone === "success"
                        ? patientDesktopPalette.linkBlue
                        : patientDesktopPalette.strongBlue;

                    return (
                      <Pressable
                        accessibilityLabel={action.labelText}
                        accessibilityRole="button"
                        key={action.labelText}
                        onPress={action.onPress}
                        style={[
                          {
                            alignSelf: "flex-start",
                            backgroundColor: patientDesktopPalette.lightBand,
                            borderRadius: 14,
                            paddingHorizontal: designTokens.spacing.md,
                            paddingVertical: 7,
                          },
                        ]}
                      >
                        <Text
                          numberOfLines={1}
                          style={[
                            styles.navigationText,
                            {
                              color: actionColor,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 13,
                              fontWeight: "600",
                              lineHeight: 17,
                            },
                          ]}
                        >
                          {action.labelText}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              ) : null;
            const cellContent = actionButtons ?? (
              <>
                <Text
                  numberOfLines={2}
                  style={[
                    styles.navigationText,
                    {
                      alignSelf: isActionCell ? "flex-start" : undefined,
                      backgroundColor: isActionCell
                        ? patientDesktopPalette.lightBand
                        : "transparent",
                      borderRadius: isActionCell ? 14 : 0,
                      color: primaryColor,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: isEmphasisColumn ? 16 : 14,
                      fontWeight: isEmphasisColumn
                        ? "700"
                        : isLeadColumn
                          ? "600"
                          : "500",
                      lineHeight: isEmphasisColumn ? 21 : 19,
                      paddingHorizontal: isActionCell ? 14 : 0,
                      paddingVertical: isActionCell ? 7 : 0,
                    },
                  ]}
                >
                  {cell.primaryText}
                </Text>
                {cell.secondaryText ? (
                  <Text
                    numberOfLines={2}
                    style={[
                      styles.metricLabel,
                      {
                        color: palette.mutedText,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 12,
                        fontWeight: "400",
                        lineHeight: 16,
                      },
                    ]}
                  >
                    {cell.secondaryText}
                  </Text>
                ) : null}
              </>
            );

            return (
              <View
                key={`${row.id}-${column.id}`}
                style={[
                  styles.patientDesktopDataTableCell,
                  {
                    flex: column.flex,
                  },
                ]}
              >
                {isPressableAction ? (
                  <Pressable
                    accessibilityLabel={cell.primaryText}
                    accessibilityRole="button"
                    onPress={cell.onPress}
                    style={[
                      {
                        alignSelf: "flex-start",
                        gap: 3,
                      },
                    ]}
                  >
                    {cellContent}
                  </Pressable>
                ) : (
                  cellContent
                )}
              </View>
            );
          })}
        </View>
      ))}
    </View>
  );
}

function renderPatientDesktopSeeMoreButton(input: {
  layout: typeof designTokens.semanticLayout;
  onPress: () => void;
  palette: PatientDesktopPalette;
}) {
  return (
    <Pressable
      onPress={input.onPress}
      style={[
        styles.patientDesktopSeeMoreButton,
        {
          backgroundColor: "transparent",
          borderColor: "transparent",
          borderRadius: designTokens.radius.pill,
          paddingHorizontal: 6,
          paddingVertical: 5,
        },
      ]}
    >
      <Text
        numberOfLines={1}
        style={[
          styles.metricLabel,
          {
            color: input.palette.linkBlue,
            fontFamily: input.layout.bodyFontFamily,
            fontSize: 12,
            fontWeight: "700",
          },
        ]}
      >
        {`See all ${String.fromCodePoint(0x2192)}`}
      </Text>
    </Pressable>
  );
}

function renderPatientDesktopCareDetailModal(input: {
  layout: typeof designTokens.semanticLayout;
  modalState: PatientDesktopCareDetailModalState;
  onClose: () => void;
  onNavigateMessages: () => void;
  onNoticeChange: (value: string | null) => void;
  onSendMessage: (input: PatientMessageCommandInput) => Promise<boolean>;
  palette: PatientDesktopPalette;
}) {
  const {
    layout,
    modalState,
    onClose,
    onNavigateMessages,
    onNoticeChange,
    onSendMessage,
    palette,
  } = input;

  if (!modalState) {
    return null;
  }

  const requestCallback = () => {
    const bodyText = `Please ask my care team to call me back about ${modalState.titleText}.`;

    onNoticeChange(`Sending callback request to ${brand.assistantName}.`);
    void onSendMessage({ bodyText }).then((wasApplied) => {
      if (wasApplied) {
        onClose();
        onNavigateMessages();
        onNoticeChange(
          `Callback request saved. Check messages for ${brand.assistantName}'s response.`,
        );
        return;
      }

      onNoticeChange(
        "The callback request could not be saved. Please retry after the workspace refreshes.",
      );
    });
  };
  const reviewWithAi = () => {
    onNoticeChange(`Sending ${modalState.kind} review to ${brand.assistantName}.`);
    void onSendMessage({ bodyText: modalState.reviewPromptText }).then(
      (wasApplied) => {
        if (wasApplied) {
          onClose();
          onNavigateMessages();
          onNoticeChange(
            `Review request saved. Check messages for ${brand.assistantName}'s response.`,
          );
          return;
        }

        onNoticeChange(
          "The review request could not be saved. Please retry after the workspace refreshes.",
        );
      },
    );
  };

  return (
    <Modal
      animationType="fade"
      onRequestClose={onClose}
      transparent={true}
      visible={true}
    >
      <View style={styles.patientSupportModalOverlay}>
        <Pressable
          accessibilityLabel="Close patient detail"
          onPress={onClose}
          style={styles.patientSupportModalBackdrop}
        />
        <View
          style={[
            styles.patientSupportModalPanel,
            {
              backgroundColor: palette.white,
              borderColor: palette.line,
              borderRadius: designTokens.radius.md,
              maxWidth: 720,
            },
          ]}
        >
          <View
            style={[
              styles.patientSupportModalHeader,
              {
                backgroundColor: palette.darkBlue,
                paddingHorizontal: 20,
                paddingVertical: designTokens.spacing.lg,
              },
            ]}
          >
            <View style={[styles.patientIdentityCopy, { flex: 1, gap: designTokens.spacing.xs }]}>
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: patientMobilePalette.footerMuted,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 12,
                    fontWeight: "700",
                    textTransform: "uppercase",
                  },
                ]}
              >
                {modalState.badgeText}
              </Text>
              <Text
                style={[
                  styles.sectionTitle,
                  {
                    color: palette.white,
                    fontFamily: layout.displayFontFamily,
                    fontSize: 22,
                    lineHeight: 28,
                  },
                ]}
              >
                {modalState.titleText}
              </Text>
              {modalState.subtitleText ? (
                <Text
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: patientMobilePalette.footerMuted,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 13,
                      lineHeight: 18,
                    },
                  ]}
                >
                  {modalState.subtitleText}
                </Text>
              ) : null}
            </View>
            <Pressable
              accessibilityLabel="Close patient detail"
              onPress={onClose}
              style={[
                styles.patientSupportModalCloseButton,
                {
                  backgroundColor: palette.white,
                  borderColor: palette.white,
                  borderRadius: 6,
                  height: 36,
                  width: 36,
                },
              ]}
            >
              <Text
                style={[
                  styles.navigationText,
                  {
                    color: palette.darkBlue,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 22,
                    fontWeight: "500",
                  },
                ]}
              >
                X
              </Text>
            </Pressable>
          </View>

          <ScrollView
            style={styles.patientSupportModalScroll}
            contentContainerStyle={[
              styles.patientSupportModalContent,
              {
                gap: 14,
                padding: 20,
              },
            ]}
          >
            {modalState.detailRows.map((row) => (
              <View
                key={row.id}
                style={[
                  styles.patientMobileDocumentTextBox,
                  {
                    backgroundColor: patientDesktopPalette.lightBand,
                    borderColor: palette.line,
                    borderRadius: 6,
                    gap: designTokens.spacing.xs,
                    padding: designTokens.spacing.md,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: patientDesktopPalette.linkBlue,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 11,
                      fontWeight: "800",
                      textTransform: "uppercase",
                    },
                  ]}
                >
                  {row.labelText}
                </Text>
                <Text
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: palette.primaryText,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 14,
                      lineHeight: 20,
                    },
                  ]}
                >
                  {row.valueText}
                </Text>
              </View>
            ))}
            <View style={[styles.patientRowFooter, { gap: 10 }]}>
              <Pressable
                accessibilityLabel={`Review ${modalState.titleText} with AI`}
                onPress={reviewWithAi}
                style={[
                  styles.inlinePill,
                  {
                    backgroundColor: patientMobilePalette.actionBlue,
                    borderColor: patientMobilePalette.actionBlue,
                    borderRadius: designTokens.radius.md,
                    paddingHorizontal: designTokens.spacing.lg,
                    paddingVertical: 11,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.navigationText,
                    {
                      color: palette.white,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 14,
                      fontWeight: "800",
                    },
                  ]}
                >
                  Review with AI
                </Text>
              </Pressable>
              <Pressable
                accessibilityLabel={`Request callback about ${modalState.titleText}`}
                onPress={requestCallback}
                style={[
                  styles.inlinePill,
                  {
                    backgroundColor: patientDesktopPalette.lightBand,
                    borderColor: palette.line,
                    borderRadius: designTokens.radius.md,
                    paddingHorizontal: designTokens.spacing.lg,
                    paddingVertical: 11,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.navigationText,
                    {
                      color: patientDesktopPalette.linkBlue,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 14,
                      fontWeight: "800",
                    },
                  ]}
                >
                  Request callback
                </Text>
              </Pressable>
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

function renderPatientWorkspaceDesktopAgentSurface(input: {
  conversationMessages: ChatMessageViewModel[];
  contentMaxWidth: number;
  draftValue: string;
  isMessageCommandSubmitting: boolean;
  layout: typeof designTokens.semanticLayout;
  navItems: readonly PatientWorkspaceNavigationItem[];
  noticeText: string | null;
  onConversationContentSizeChange: () => void;
  onDraftChange: (value: string) => void;
  onNavigateTab: (value: PatientWorkspaceTabId) => void;
  onNoticeChange: (value: string | null) => void;
  onOpenSupportModal: (input: PatientSupportModalState) => void;
  readAloudControls: PatientWorkspaceReadAloudControls;
  onSendMessage: (input: PatientMessageCommandInput) => Promise<boolean>;
  patientConversationScrollRef: RefObject<ScrollView | null>;
  palette: PatientDesktopPalette;
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
  voiceInputControls: PatientWorkspaceVoiceInputControls;
  viewportHeight: number;
}) {
  const {
    conversationMessages,
    contentMaxWidth,
    draftValue,
    isMessageCommandSubmitting,
    layout,
    navItems,
    noticeText,
    onConversationContentSizeChange,
    onDraftChange,
    onNavigateTab,
    onNoticeChange,
    onOpenSupportModal,
    readAloudControls,
    onSendMessage,
    patientConversationScrollRef,
    palette,
    summary,
    voiceInputControls,
    viewportHeight,
  } = input;
  const agentPaperHeight = Math.max(
    360,
    Math.min(680, Math.round(viewportHeight * 0.52)),
  );
  const canSendMessage =
    draftValue.trim().length > 0 &&
    !isMessageCommandSubmitting &&
    summary != null;
  const navTileFlexBasis = `${
    100 / Math.max(navItems.length, 1)
  }%` as `${number}%`;
  const sendMessage = () => {
    const trimmedDraft = draftValue.trim();

    if (!trimmedDraft) {
      onNoticeChange("Enter a question before sending.");
      return;
    }

    if (!summary) {
      onNoticeChange("No active patient workspace is available for messaging.");
      return;
    }

    onNoticeChange(`Sending message to ${brand.assistantName}.`);

    void onSendMessage({ bodyText: trimmedDraft }).then((wasApplied) => {
      if (wasApplied) {
        onDraftChange("");
        onNoticeChange(
          `Message saved. Check the conversation for ${brand.assistantName}'s response.`,
        );
        return;
      }

      onNoticeChange(
        "The message could not be saved. Please retry after the workspace refreshes.",
      );
    });
  };
  const sendQuickReply = (questionText: string) => {
    if (!summary) {
      onNoticeChange("No active patient workspace is available for messaging.");
      return;
    }

    onNoticeChange(`Sending message to ${brand.assistantName}.`);

    void onSendMessage({ bodyText: questionText }).then((wasApplied) => {
      onNoticeChange(
        wasApplied
          ? `Message saved. Check the conversation for ${brand.assistantName}'s response.`
          : "The message could not be saved. Please retry after the workspace refreshes.",
      );
    });
  };
  const quickReplySuggestions = [
    "When is my next appointment?",
    "Review my medications",
    "Explain my prep documents",
  ];
  return (
    <View
      style={[
        styles.patientDesktopAgentRoot,
        {
          backgroundColor: palette.agentBlue,
        },
      ]}
    >
      <View
        style={[
          styles.patientDesktopAgentContainer,
          {
            flex: 1,
            gap: 18,
            maxWidth: contentMaxWidth,
            paddingBottom: designTokens.spacing.xl,
            paddingHorizontal: designTokens.spacing.none,
            paddingTop: 28,
          },
        ]}
      >
        <View
          style={[
            styles.patientDesktopAgentTopSection,
            createWebOrderStyle(3),
            {
              justifyContent: "flex-end",
              paddingHorizontal: designTokens.spacing.none,
            },
          ]}
        >
          <View
            style={[
              styles.patientDesktopAgentControls,
              {
                gap: designTokens.spacing.lg,
              },
            ]}
          >
            <Pressable
              accessibilityLabel={
                readAloudControls.isAutoReadAloudEnabled
                  ? "Turn off auto message read aloud"
                  : "Turn on auto message read aloud"
              }
              onPress={readAloudControls.toggleAutoReadAloud}
              style={[
                styles.patientDesktopAgentIconButton,
                {
                  backgroundColor: patientMobilePalette.actionBlue,
                  borderColor: patientMobilePalette.actionBlue,
                  borderRadius: 27,
                  height: 54,
                  width: 54,
                },
              ]}
            >
              {renderPatientDesktopSpeakerGlyph({
                color: palette.white,
                isActive: readAloudControls.isAutoReadAloudEnabled,
              })}
            </Pressable>
            <Pressable
              onPress={() => onNavigateTab("overview")}
              style={[
                styles.patientDesktopAgentIconButton,
                {
                  backgroundColor: patientMobilePalette.actionBlue,
                  borderColor: patientMobilePalette.actionBlue,
                  borderRadius: 27,
                  height: 54,
                  width: 54,
                },
              ]}
            >
              <LineIcon
                color={palette.white}
                name="x"
                size={28}
                strokeWidth={2.35}
              />
            </Pressable>
          </View>
        </View>

        {noticeText ? (
          <Text
            style={[
              styles.metricLabel,
              {
                color: patientMobilePalette.primaryText,
                fontFamily: layout.bodyFontFamily,
                fontSize: 16,
                fontWeight: "500",
                textAlign: "center",
              },
            ]}
          >
            {noticeText}
          </Text>
        ) : null}

        <View
          style={[
            styles.patientDesktopAgentPaper,
            createWebOrderStyle(1),
            {
              backgroundColor: palette.white,
              borderRadius: designTokens.radius.xl,
              flexGrow: 1,
              flexShrink: 1,
              gap: designTokens.spacing.none,
              minHeight: agentPaperHeight,
              padding: designTokens.spacing.lg,
            },
          ]}
        >
          <View
            style={[
              styles.patientDesktopAgentVisualRail,
              {
                alignSelf: "stretch",
                backgroundColor: palette.white,
                borderColor: palette.white,
                borderRadius: designTokens.radius.md,
                justifyContent: "center",
                maxHeight: 540,
                maxWidth: 320,
                padding: designTokens.spacing.none,
                width: "32%",
              },
            ]}
          >
            {renderPatientDesktopAgentAvatarMedia()}
          </View>

          <View
            style={[
              styles.patientDesktopAgentConversation,
              {
                gap: designTokens.spacing.none,
              },
            ]}
          >
            <View
              style={[
                styles.patientRowHeader,
                {
                  alignItems: "center",
                  borderBottomColor: patientDesktopPalette.line,
                  borderBottomWidth: 1,
                  marginBottom: 10,
                  paddingBottom: designTokens.spacing.sm,
                },
              ]}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: patientDesktopPalette.linkBlue,
                    flex: 1,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 12,
                    fontWeight: "700",
                    textTransform: "uppercase",
                  },
                ]}
              >
                Full history
              </Text>
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: patientMobilePalette.primaryText,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 12,
                    fontWeight: "600",
                  },
                ]}
              >
                {`${conversationMessages.length} message${
                  conversationMessages.length === 1 ? "" : "s"
                }`}
              </Text>
            </View>
            <ScrollView
              ref={patientConversationScrollRef}
              onContentSizeChange={onConversationContentSizeChange}
              showsVerticalScrollIndicator
              style={styles.patientDesktopAgentMessageList}
              contentContainerStyle={[
                styles.listGroup,
                {
                  gap: 10,
                  paddingRight: designTokens.spacing.none,
                },
              ]}
            >
              {conversationMessages.length > 0 ? (
                conversationMessages.map((message) => {
                  const isPatientMessage = message.senderKind === "patient";
                  const isSystemMessage = message.senderKind === "system";
                  const isReadingAloudThisMessage =
                    readAloudControls.activeReadAloudMessageId === message.id &&
                    readAloudControls.isReadingAloud;
                  const bubbleColor = isPatientMessage ? patientDesktopPalette.linkBlue : patientDesktopPalette.lightBand;
                  const bodyColor = isPatientMessage ? patientDesktopPalette.body : patientMobilePalette.primaryText;
                  const metaColor = isPatientMessage ? patientMobilePalette.footerMuted : patientDesktopPalette.linkBlue;
                  const senderText = isPatientMessage
                    ? "You"
                    : message.senderText || brand.assistantName;

                  return (
                    <View
                      key={message.id}
                      style={{
                        alignSelf: isSystemMessage
                          ? "center"
                          : isPatientMessage
                            ? "flex-end"
                            : "flex-start",
                        maxWidth: isSystemMessage ? "100%" : "78%",
                        width: isSystemMessage ? "100%" : undefined,
                      }}
                    >
                      <View
                        style={[
                          styles.patientDesktopAgentBubble,
                          {
                            backgroundColor: bubbleColor,
                            borderColor: bubbleColor,
                            borderRadius: designTokens.radius.md,
                            paddingHorizontal: designTokens.spacing.lg,
                            paddingVertical: designTokens.spacing.md,
                          },
                        ]}
                      >
                        <View
                          style={[
                            styles.patientRowHeader,
                            {
                              alignItems: "center",
                              gap: designTokens.spacing.md,
                            },
                          ]}
                        >
                          <Text
                            numberOfLines={1}
                            style={[
                              styles.metricLabel,
                              {
                                color: metaColor,
                                flex: 1,
                                fontFamily: layout.bodyFontFamily,
                                fontSize: 13,
                                fontWeight: "700",
                              },
                            ]}
                          >
                            {senderText}
                          </Text>
                          {!isPatientMessage && !isSystemMessage ? (
                            <Pressable
                              onPress={() =>
                                readAloudControls.readMessageAloud(message)
                              }
                              style={[
                                styles.patientDesktopMessageReadButton,
                                {
                                  backgroundColor: palette.white,
                                  borderColor: palette.white,
                                  borderRadius: designTokens.radius.sm,
                                  gap: designTokens.spacing.xs,
                                  paddingHorizontal: 9,
                                  paddingVertical: 5,
                                },
                              ]}
                            >
                              <Text
                                style={[
                                  styles.metricLabel,
                                  {
                                    color: patientDesktopPalette.linkBlue,
                                    fontFamily: layout.bodyFontFamily,
                                    fontSize: 10,
                                    fontWeight: "700",
                                    textTransform: "uppercase",
                                  },
                                ]}
                              >
                                {isReadingAloudThisMessage
                                  ? "Stop"
                                  : "Read aloud"}
                              </Text>
                              <LineIcon
                                color={patientDesktopPalette.linkBlue}
                                name="chevron-right"
                                size={12}
                                strokeWidth={2.4}
                              />
                            </Pressable>
                          ) : null}
                          <Text
                            style={[
                              styles.metricLabel,
                              {
                                color: metaColor,
                                fontFamily: layout.bodyFontFamily,
                                fontSize: 11,
                              },
                            ]}
                          >
                            {message.timestampText}
                          </Text>
                        </View>
                        <Text
                          style={[
                            styles.sectionSubtitle,
                            {
                              color: bodyColor,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 15,
                              fontWeight: "400",
                              lineHeight: 21,
                            },
                          ]}
                        >
                          {message.bodyText}
                        </Text>
                        {message.senderKind !== "patient" ? (
                          <View
                            style={[
                              styles.patientRowFooter,
                              {
                                alignItems: "center",
                                gap: designTokens.spacing.sm,
                              },
                            ]}
                          >
                            <Text
                              style={[
                                styles.metricLabel,
                                {
                                  color: patientDesktopPalette.linkBlue,
                                  fontFamily: layout.bodyFontFamily,
                                  fontSize: 11,
                                  fontWeight: "400",
                                },
                              ]}
                            >
                              Rate this answer:
                            </Text>
                            {(["down", "up"] as const).map((direction) => (
                              <Pressable
                                key={direction}
                                onPress={() =>
                                  onOpenSupportModal({
                                    kind: "chat-feedback",
                                    message,
                                    sentiment:
                                      direction === "up"
                                        ? "helpful"
                                        : "needs-work",
                                    source: `patient-desktop-agent:${direction}`,
                                  })
                                }
                                style={[
                                  styles.patientDesktopMessageFeedbackIcon,
                                  {
                                    backgroundColor: palette.white,
                                    borderColor: palette.white,
                                    borderRadius: designTokens.radius.sm,
                                    height: 24,
                                    width: 26,
                                  },
                                ]}
                              >
                                {renderPatientDesktopFeedbackGlyph({
                                  color: patientDesktopPalette.linkBlue,
                                  direction,
                                })}
                              </Pressable>
                            ))}
                            <Pressable
                              onPress={() =>
                                onOpenSupportModal({
                                  kind: "chat-feedback",
                                  message,
                                  source: "patient-desktop-agent:send-feedback",
                                })
                              }
                              style={[
                                styles.patientDesktopMessageReadButton,
                                {
                                  backgroundColor: palette.white,
                                  borderColor: palette.white,
                                  borderRadius: designTokens.radius.sm,
                                  paddingHorizontal: 9,
                                  paddingVertical: 5,
                                },
                              ]}
                            >
                              <Text
                                style={[
                                  styles.metricLabel,
                                  {
                                    color: patientDesktopPalette.linkBlue,
                                    fontFamily: layout.bodyFontFamily,
                                    fontSize: 11,
                                    fontWeight: "700",
                                    textTransform: "uppercase",
                                  },
                                ]}
                              >
                                SEND FEEDBACK
                              </Text>
                            </Pressable>
                          </View>
                        ) : null}
                      </View>
                    </View>
                  );
                })
              ) : (
                <View
                  style={[
                    styles.patientDesktopAgentEmptyMessage,
                    {
                      backgroundColor: patientDesktopPalette.body,
                      borderColor: patientMobilePalette.line,
                      borderRadius: 6,
                      padding: 18,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.sectionSubtitle,
                      {
                        color: palette.mutedText,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 15,
                        lineHeight: 22,
                      },
                    ]}
                  >
                    {`Hi there. I am your ${brand.assistantName}. How can I assist you`}
                    today?
                  </Text>
                </View>
              )}
            </ScrollView>
          </View>
        </View>

        <View
          style={[
            styles.patientDesktopAgentComposer,
            createWebOrderStyle(2),
            {
              alignItems: "stretch",
              flexDirection: "column",
              gap: designTokens.spacing.md,
            },
          ]}
        >
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: designTokens.spacing.sm,
            }}
          >
            {quickReplySuggestions.map((questionText) => (
              <Pressable
                accessibilityRole="button"
                key={questionText}
                onPress={() => sendQuickReply(questionText)}
                style={{
                  alignItems: "center",
                  backgroundColor: palette.white,
                  borderColor: palette.lightBlue,
                  borderRadius: designTokens.radius.pill,
                  borderWidth: 1.5,
                  paddingHorizontal: 14,
                  paddingVertical: designTokens.spacing.sm,
                }}
              >
                <Text
                  style={[
                    styles.navigationText,
                    {
                      color: palette.linkBlue,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 13,
                      fontWeight: "700",
                    },
                  ]}
                >
                  {questionText}
                </Text>
              </Pressable>
            ))}
          </View>
          <View
            style={{
              alignItems: "stretch",
              flexDirection: "row",
              gap: 14,
              width: "100%",
            }}
          >
            <View
              style={[
                styles.patientDesktopAgentInputShell,
                {
                  backgroundColor: palette.white,
                  borderColor: palette.white,
                  borderRadius: 26,
                  flex: 1,
                  flexBasis: 0,
                  minHeight: 52,
                  minWidth: 320,
                  paddingLeft: 20,
                  paddingRight: designTokens.spacing.md,
                },
              ]}
            >
              <TextInput
                multiline={false}
                onChangeText={onDraftChange}
                onSubmitEditing={sendMessage}
                placeholder="Type a message..."
                placeholderTextColor={patientMobilePalette.actionBlueMuted}
                style={[
                  styles.searchInput,
                  {
                    color: palette.primaryText,
                    flex: 1,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 16,
                    minHeight: 44,
                  },
                ]}
                value={draftValue}
              />
              <Pressable
                accessibilityLabel={
                  voiceInputControls.isListening
                    ? "Stop voice input"
                    : "Start voice input"
                }
                onPress={voiceInputControls.toggleListening}
                style={[
                  styles.patientDesktopAgentSmallButton,
                  {
                    backgroundColor: voiceInputControls.isListening
                      ? patientMobilePalette.softBlue
                      : "transparent",
                    borderColor: "transparent",
                    borderRadius: 19,
                    height: 38,
                    width: 38,
                  },
                ]}
              >
                {renderPatientDesktopMicrophoneGlyph(
                  patientDesktopPalette.black,
                  voiceInputControls.isListening,
                )}
              </Pressable>
            </View>
            <Pressable
              disabled={!canSendMessage}
              onPress={sendMessage}
              style={[
                styles.patientDesktopAgentSendButton,
                {
                  backgroundColor: patientMobilePalette.actionBlue,
                  borderColor: patientMobilePalette.actionBlue,
                  borderRadius: 26,
                  height: 52,
                  minWidth: 170,
                  opacity: canSendMessage ? 1 : 0.7,
                },
              ]}
            >
              <Text
                style={[
                  styles.navigationText,
                  {
                    color: palette.white,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 17,
                    fontWeight: "700",
                  },
                ]}
              >
                {isMessageCommandSubmitting ? "SENDING" : "SEND"}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </View>
  );
}

function renderPatientWorkspaceDesktopTabSurface(input: {
  contentMaxWidth: number;
  isUploadSubmitting: boolean;
  layout: typeof designTokens.semanticLayout;
  noticeText: string | null;
  onDownloadDocument: (document: PatientWorkspaceDocumentSummary) => void;
  onNavigateTab: (value: PatientWorkspaceTabId) => void;
  onOpenCareDetailModal: (
    detail: NonNullable<PatientDesktopCareDetailModalState>,
  ) => void;
  onUpdateReminderTime: (
    input: PatientMedicationReminderTimeUpdateInput,
  ) => void;
  onUploadDocument: () => void;
  palette: PatientDesktopPalette;
  selectedPatientWorkspaceTabId: PatientWorkspaceTabId;
  selectedTitle: string;
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
  tabContent: ReactNode;
}) {
  const {
    contentMaxWidth,
    isUploadSubmitting,
    layout,
    noticeText,
    onDownloadDocument,
    onNavigateTab,
    onOpenCareDetailModal,
    onUpdateReminderTime,
    onUploadDocument,
    palette,
    selectedPatientWorkspaceTabId,
    selectedTitle,
    summary,
    tabContent,
  } = input;
  const appointmentColumns = [
    { id: "date", labelText: "Date", flex: 1 },
    { id: "time", labelText: "Time", flex: 0.9 },
    { id: "name", labelText: "Name", flex: 1.9 },
    { id: "location", labelText: "Location", flex: 1.7 },
    { id: "physician", labelText: "Receiving physician", flex: 1.6 },
    { id: "actions", labelText: "Actions", flex: 1.1 },
  ];
  const procedureColumns = [
    { id: "date", labelText: "Date", flex: 1 },
    { id: "time", labelText: "Time", flex: 0.9 },
    { id: "procedure", labelText: "Procedure", flex: 2 },
    { id: "location", labelText: "Location", flex: 1.7 },
    { id: "clinician", labelText: "Leading surgeon", flex: 1.6 },
    { id: "actions", labelText: "Actions", flex: 1.1 },
  ];
  const medicationColumns = [
    { id: "time", labelText: "Time", flex: 0.9 },
    { id: "name", labelText: "Name", flex: 1.5 },
    { id: "dosage", labelText: "Dosage", flex: 1.2 },
    { id: "instructions", labelText: "Instructions", flex: 2.5 },
    { id: "reminders", labelText: "Reminders", flex: 1.05 },
    { id: "actions", labelText: "Actions", flex: 1.1 },
  ];
  const documentColumns = [
    { id: "date", labelText: "Date/time", flex: 1.25 },
    { id: "name", labelText: "Name", flex: 2.2 },
    { id: "type", labelText: "Type", flex: 1.1 },
    { id: "createdBy", labelText: "Created by", flex: 1.5 },
    { id: "actions", labelText: "Actions", flex: 1.1 },
  ];
  const selectedMedicationWakeUpTime =
    summary?.onboarding.preferences.wakeUpTime ??
    patientDesktopWakeUpTimeOptions[0].wakeUpTime;
  const medicationRemindersEnabled =
    summary?.onboarding.preferences.remindersEnabled !== false;
  const tableConfig =
    selectedPatientWorkspaceTabId === "appointments"
      ? {
          columns: appointmentColumns,
          rows: buildPatientDesktopAppointmentTableRows(
            summary,
            onOpenCareDetailModal,
          ),
          subtitle: summary?.programLabel || "Appointment schedule",
          title: "Your upcoming appointments",
        }
      : selectedPatientWorkspaceTabId === "procedures"
        ? {
            columns: procedureColumns,
            rows: buildPatientDesktopProcedureTableRows(
              summary,
              onOpenCareDetailModal,
            ),
            subtitle: summary?.programLabel || "Procedure schedule",
            title: "Your upcoming procedures",
          }
        : selectedPatientWorkspaceTabId === "medications"
          ? {
              columns: medicationColumns,
              rows: buildPatientDesktopMedicationTableRows(
                summary,
                onOpenCareDetailModal,
                onUpdateReminderTime,
              ),
              subtitle: summary?.medicationSummaryText || "Medication schedule",
              title: "Your current medications",
            }
          : selectedPatientWorkspaceTabId === "documents"
            ? {
                columns: documentColumns,
                rows: buildPatientDesktopDocumentTableRows(
                  summary,
                  onOpenCareDetailModal,
                  onDownloadDocument,
                ),
                subtitle:
                  (summary?.documentSummaries.length ?? 0) > 0
                    ? `${summary?.documentSummaries.length ?? 0} document records`
                    : "Document records",
                title: "Your documents",
              }
            : null;

  return (
    <ScrollView
      style={styles.patientDesktopCenterScroll}
      contentContainerStyle={[
        styles.patientDesktopTabContent,
        {
          gap: 22,
          maxWidth: contentMaxWidth,
          paddingBottom: 104,
          paddingHorizontal: designTokens.spacing.none,
          paddingTop: 36,
        },
      ]}
    >
      <View
        style={[
          styles.patientDesktopTabHeader,
          {
            borderBottomColor: tableConfig ? palette.line : "transparent",
            gap: designTokens.spacing.sm,
            paddingBottom: tableConfig ? 18 : 0,
          },
        ]}
      >
        <View
          style={[
            styles.patientDesktopTabTitleRow,
            {
              gap: designTokens.spacing.lg,
            },
          ]}
        >
          <View style={[styles.patientIdentityCopy, { gap: designTokens.spacing.sm }]}>
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: palette.primaryText,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 28,
                  fontWeight: "700",
                  letterSpacing: 0,
                  lineHeight: 34,
                },
              ]}
            >
              {tableConfig?.title ??
                (selectedPatientWorkspaceTabId === "messages"
                  ? brand.assistantName
                  : selectedTitle)}
            </Text>
            {!tableConfig ? (
              <Text
                numberOfLines={1}
                style={[
                  styles.metricLabel,
                  {
                    color: palette.mutedText,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 14,
                    lineHeight: 20,
                  },
                ]}
              >
                {summary?.programLabel || "Patient care workspace"}
              </Text>
            ) : null}
          </View>

          {selectedPatientWorkspaceTabId === "medications" ? (
            <View
              style={[
                styles.patientDesktopDocumentControls,
                {
                  gap: designTokens.spacing.md,
                },
              ]}
            >
              <Text
                style={[
                  styles.navigationText,
                  {
                    color: palette.primaryText,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 16,
                    fontWeight: "400",
                    lineHeight: 22,
                  },
                ]}
              >
                Set up wake-up time:
              </Text>
              {patientDesktopWakeUpTimeOptions.map((option) => {
                const isSelected =
                  medicationRemindersEnabled &&
                  option.wakeUpTime === selectedMedicationWakeUpTime;

                return (
                  <Pressable
                    key={option.wakeUpTime}
                    accessibilityLabel={`Set medication reminder to ${option.displayTime}`}
                    accessibilityRole="button"
                    onPress={() =>
                      onUpdateReminderTime({
                        displayTime: option.displayTime,
                        label: "Morning",
                        slotId: "morning",
                        wakeUpTime: option.wakeUpTime,
                      })
                    }
                    style={[
                      styles.patientDesktopMedicationTimeButton,
                      {
                        backgroundColor: isSelected
                          ? patientMobilePalette.actionBlue
                          : palette.white,
                        borderColor: isSelected
                          ? patientMobilePalette.actionBlue
                          : palette.line,
                        borderRadius: designTokens.radius.md,
                        minHeight: 40,
                        minWidth: 80,
                        paddingHorizontal: 18,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.navigationText,
                        {
                          color: isSelected
                            ? palette.white
                            : patientMobilePalette.actionBlueMuted,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 16,
                          fontWeight: "500",
                        },
                      ]}
                    >
                      {option.displayTime}
                    </Text>
                  </Pressable>
                );
              })}
              <Pressable
                accessibilityLabel="Turn off medication reminders"
                accessibilityRole="button"
                onPress={() =>
                  onUpdateReminderTime({
                    displayTime: "off",
                    label: "Medication",
                    slotId: "morning",
                    wakeUpTime: "off",
                  })
                }
                style={[
                  styles.patientDesktopMedicationTimeButton,
                  {
                    backgroundColor: medicationRemindersEnabled
                      ? palette.white
                      : patientMobilePalette.actionBlue,
                    borderColor: medicationRemindersEnabled
                      ? palette.line
                      : patientMobilePalette.actionBlue,
                    borderRadius: designTokens.radius.md,
                    minHeight: 40,
                    minWidth: 118,
                    paddingHorizontal: 18,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.navigationText,
                    {
                      color: medicationRemindersEnabled
                        ? patientMobilePalette.actionBlueMuted
                        : palette.white,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 16,
                      fontWeight: "500",
                    },
                  ]}
                >
                  Off
                </Text>
              </Pressable>
            </View>
          ) : selectedPatientWorkspaceTabId === "documents" ? (
            <View
              style={[
                styles.patientDesktopDocumentControls,
                {
                  gap: designTokens.spacing.md,
                },
              ]}
            >
              <Pressable
                accessibilityLabel="Upload document"
                accessibilityRole="button"
                disabled={isUploadSubmitting}
                onPress={onUploadDocument}
                style={[
                  styles.patientDesktopMedicationTimeButton,
                  {
                    backgroundColor: isUploadSubmitting
                      ? patientMobilePalette.actionSoft
                      : patientMobilePalette.actionBlue,
                    borderColor: isUploadSubmitting
                      ? palette.line
                      : patientMobilePalette.actionBlue,
                    borderRadius: designTokens.radius.md,
                    minHeight: 40,
                    minWidth: 156,
                    opacity: isUploadSubmitting ? 0.82 : 1,
                    paddingHorizontal: 18,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.navigationText,
                    {
                      color: isUploadSubmitting
                        ? patientMobilePalette.actionBlueMuted
                        : palette.white,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 16,
                      fontWeight: "700",
                    },
                  ]}
                >
                  {isUploadSubmitting ? "Uploading" : "Upload document"}
                </Text>
              </Pressable>
            </View>
          ) : null}
        </View>
      </View>

      {noticeText ? (
        <Text
          style={[
            styles.sectionSubtitle,
            {
              color: patientDesktopPalette.infoBlue,
              fontFamily: layout.bodyFontFamily,
              fontSize: 14,
              lineHeight: 20,
            },
          ]}
        >
          {noticeText}
        </Text>
      ) : null}

      {tableConfig
        ? renderPatientDesktopTable({
            columns: tableConfig.columns,
            layout,
            palette,
            rows: tableConfig.rows,
          })
        : tabContent}
    </ScrollView>
  );
}

function renderPatientWorkspaceDesktopHome(input: {
  contentMaxWidth: number;
  layout: typeof designTokens.semanticLayout;
  onDownloadDocument: (document: PatientWorkspaceDocumentSummary) => void;
  onNavigateTab: (value: PatientWorkspaceTabId) => void;
  onOpenCareDetailModal: (
    detail: NonNullable<PatientDesktopCareDetailModalState>,
  ) => void;
  palette: PatientDesktopPalette;
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
}) {
  const {
    contentMaxWidth,
    layout,
    onDownloadDocument,
    onNavigateTab,
    onOpenCareDetailModal,
    palette,
    summary,
  } = input;
  const givenName = resolvePatientWorkspaceGivenName(summary?.displayName);
  const calendarRows = buildPatientDesktopCalendarRows(summary);
  const medicationRows = buildPatientDesktopMedicationRows(summary)
    .filter((row) => !row.isHistorical)
    .slice(0, 3);
  const procedureRows = buildPatientDesktopProcedureTableRows(
    summary,
    onOpenCareDetailModal,
  ).slice(0, 2);
  const documentRows = buildPatientDesktopDocumentTableRows(
    summary,
    onOpenCareDetailModal,
    onDownloadDocument,
  ).slice(0, 2);
  const callRows = (summary?.voiceCallSummaries ?? []).slice(0, 2);

  return (
    <ScrollView
      style={styles.patientDesktopCenterScroll}
      contentContainerStyle={[
        styles.patientDesktopDashboard,
        {
          gap: designTokens.spacing.xl,
          maxWidth: contentMaxWidth,
          paddingBottom: 76,
          paddingHorizontal: designTokens.spacing.none,
          paddingTop: 36,
        },
      ]}
    >
      <View style={[styles.listGroup, { gap: designTokens.spacing.xs }]}>
        <Text
          style={[
            styles.sectionTitle,
            {
              color: palette.primaryText,
              fontFamily: layout.displayFontFamily,
              fontSize: 26,
              fontWeight: "800",
              letterSpacing: 0,
              lineHeight: 32,
              textAlign: "left",
            },
          ]}
        >
          {`Good afternoon, ${givenName}`}
        </Text>
        <Text
          style={[
            styles.metricLabel,
            {
              color: palette.mutedText,
              fontFamily: layout.bodyFontFamily,
              fontSize: 13,
              lineHeight: 18,
            },
          ]}
        >
          Here's your care overview.
        </Text>
      </View>

      <View style={{ flexDirection: "row", gap: 14 }}>
        {[
          {
            emoji: String.fromCodePoint(0x1f3e5),
            label: "Next appointment",
            value: calendarRows[0]
              ? `${calendarRows[0].dateText} · ${calendarRows[0].timeText}`
              : "None scheduled",
            sub: calendarRows[0]?.titleText ?? "Your next visit appears here",
          },
          {
            emoji: String.fromCodePoint(0x1f48a),
            label: "Next dose",
            value: medicationRows[0]?.titleText ?? "No medications",
            sub: medicationRows[0]?.routineText ?? "Guidance pending",
          },
          {
            emoji: String.fromCodePoint(0x1f4c4),
            label: "Documents",
            value: `${documentRows.length} ${
              documentRows.length === 1 ? "item" : "items"
            }`,
            sub: documentRows[0]?.cells?.[1]?.primaryText ?? "Nothing to review",
          },
        ].map((glance) => (
          <View
            key={glance.label}
            style={{
              alignItems: "center",
              backgroundColor: palette.white,
              borderColor: palette.line,
              borderRadius: designTokens.radius.xl,
              borderWidth: 1,
              flex: 1,
              flexDirection: "row",
              gap: designTokens.spacing.md,
              minWidth: 0,
              paddingHorizontal: designTokens.spacing.lg,
              paddingVertical: 14,
            }}
          >
            <Text style={{ fontSize: 20 }}>{glance.emoji}</Text>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text
                numberOfLines={1}
                style={[
                  styles.metricLabel,
                  {
                    color: palette.mutedText,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 10,
                    fontWeight: "700",
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                  },
                ]}
              >
                {glance.label}
              </Text>
              <Text
                numberOfLines={1}
                style={[
                  styles.navigationText,
                  {
                    color: palette.primaryText,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 14,
                    fontWeight: "800",
                  },
                ]}
              >
                {glance.value}
              </Text>
              <Text
                numberOfLines={1}
                style={[
                  styles.metricLabel,
                  {
                    color: palette.mutedText,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 11,
                  },
                ]}
              >
                {glance.sub}
              </Text>
            </View>
          </View>
        ))}
      </View>

      <View
        style={[
          styles.patientDesktopDashboardColumns,
          {
            gap: 20,
          },
        ]}
      >
        <View style={[styles.patientDesktopDashboardColumn, { gap: 18 }]}>
          <View style={styles.patientDesktopDashboardHeaderRow}>
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: palette.primaryText,
                  fontFamily: layout.displayFontFamily,
                  fontSize: 24,
                  fontWeight: "600",
                  lineHeight: 30,
                },
              ]}
            >
              Your Calendar
            </Text>
            {renderPatientDesktopSeeMoreButton({
              layout,
              onPress: () => onNavigateTab("appointments"),
              palette,
            })}
          </View>

          <View style={[styles.listGroup, { gap: designTokens.spacing.md }]}>
            {calendarRows.length > 0 ? (
              calendarRows.map((row) => (
                <View key={row.id} style={[styles.listGroup, { gap: 10 }]}>
                  <Text
                    style={[
                      styles.navigationText,
                      {
                        color: palette.primaryText,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 14,
                      },
                    ]}
                  >
                    {row.groupText}
                  </Text>
                  <Pressable
                    onPress={() => onNavigateTab("appointments")}
                    style={[
                      styles.patientDesktopScheduleCard,
                      {
                        backgroundColor: palette.white,
                        borderColor: palette.line,
                        borderRadius: 14,
                        borderWidth: 1,
                        minHeight: 60,
                        paddingHorizontal: 14,
                        paddingVertical: 10,
                      },
                    ]}
                  >
                    <View
                      style={{
                        alignItems: "center",
                        backgroundColor: palette.lightBlue,
                        borderRadius: 11,
                        height: 38,
                        justifyContent: "center",
                        width: 38,
                      }}
                    >
                      <Text style={{ fontSize: 18 }}>
                        {String.fromCodePoint(0x1f3e5)}
                      </Text>
                    </View>
                    <Text
                      numberOfLines={1}
                      style={[
                        styles.sectionTitle,
                        {
                          color: palette.primaryText,
                          fontFamily: layout.displayFontFamily,
                          fontSize: 18,
                          fontWeight: "700",
                          minWidth: 78,
                        },
                      ]}
                    >
                      {row.dateText}
                    </Text>
                    <View
                      style={[
                        styles.patientIdentityCopy,
                        { flex: 1, gap: designTokens.spacing.xxs, minWidth: 0 },
                      ]}
                    >
                      <Text
                        numberOfLines={1}
                        style={[
                          styles.navigationText,
                          {
                            color: palette.primaryText,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 15,
                            fontWeight: "700",
                          },
                        ]}
                      >
                        {row.titleText}
                      </Text>
                      <Text
                        numberOfLines={1}
                        style={[
                          styles.metricLabel,
                          {
                            color: palette.mutedText,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 11,
                          },
                        ]}
                      >
                        {[row.timeText, row.detailText]
                          .filter(Boolean)
                          .join(" - ")}
                      </Text>
                    </View>
                    <View
                      style={{
                        alignItems: "center",
                        backgroundColor: palette.lightBlue,
                        borderRadius: designTokens.radius.pill,
                        paddingHorizontal: 10,
                        paddingVertical: designTokens.spacing.xs,
                      }}
                    >
                      <Text
                        style={[
                          styles.metricLabel,
                          {
                            color: palette.linkBlue,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 10,
                            fontWeight: "700",
                          },
                        ]}
                      >
                        Scheduled
                      </Text>
                    </View>
                  </Pressable>
                </View>
              ))
            ) : (
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: palette.mutedText,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 14,
                    lineHeight: 20,
                  },
                ]}
              >
                Your next visit will appear here when the schedule is available.
              </Text>
            )}
          </View>

          <View style={[styles.listGroup, { gap: designTokens.spacing.sm }]}>
            <View style={styles.patientDesktopDashboardHeaderRow}>
              <Text
                style={[
                  styles.sectionTitle,
                  {
                    color: palette.primaryText,
                    fontFamily: layout.displayFontFamily,
                    fontSize: 24,
                    fontWeight: "600",
                    lineHeight: 30,
                  },
                ]}
              >
                Procedures
              </Text>
              {renderPatientDesktopSeeMoreButton({
                layout,
                onPress: () => onNavigateTab("procedures"),
                palette,
              })}
            </View>

            {procedureRows.map((row) => (
              <Pressable
                key={row.id}
                onPress={() => onNavigateTab("procedures")}
                style={[
                  styles.patientDesktopScheduleCard,
                  {
                    backgroundColor: palette.white,
                    borderColor: palette.line,
                    borderRadius: 14,
                    borderWidth: 1,
                    minHeight: 60,
                    paddingHorizontal: 14,
                    paddingVertical: 10,
                  },
                ]}
              >
                <View
                  style={{
                    alignItems: "center",
                    backgroundColor: palette.lightBlue,
                    borderRadius: 11,
                    height: 38,
                    justifyContent: "center",
                    width: 38,
                  }}
                >
                  <Text style={{ fontSize: 17 }}>
                    {String.fromCodePoint(0x2695, 0xfe0f)}
                  </Text>
                </View>
                <View
                  style={[
                    styles.patientIdentityCopy,
                    { flex: 1, gap: designTokens.spacing.xxs, minWidth: 0 },
                  ]}
                >
                  <Text
                    numberOfLines={1}
                    style={[
                      styles.navigationText,
                      {
                        color: palette.primaryText,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 15,
                        fontWeight: "700",
                      },
                    ]}
                  >
                    {row.cells[2]?.primaryText ?? "Procedure detail"}
                  </Text>
                  <Text
                    numberOfLines={1}
                    style={[
                      styles.metricLabel,
                      {
                        color: palette.mutedText,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 11,
                      },
                    ]}
                  >
                    {[
                      row.cells[0]?.primaryText,
                      row.cells[3]?.primaryText,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "Procedure"}
                  </Text>
                </View>
                <View
                  style={{
                    alignItems: "center",
                    backgroundColor: patientMobilePalette.statusWarningBackground,
                    borderRadius: designTokens.radius.pill,
                    paddingHorizontal: 10,
                    paddingVertical: designTokens.spacing.xs,
                  }}
                >
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: patientMobilePalette.statusWarningText,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 10,
                        fontWeight: "700",
                      },
                    ]}
                  >
                    Upcoming
                  </Text>
                </View>
              </Pressable>
            ))}
          </View>
        </View>

        <View style={[styles.patientDesktopDashboardColumn, { gap: 18 }]}>
          <View style={styles.patientDesktopDashboardHeaderRow}>
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: palette.primaryText,
                  fontFamily: layout.displayFontFamily,
                  fontSize: 24,
                  fontWeight: "600",
                  lineHeight: 30,
                },
              ]}
            >
              Medications
            </Text>
            {renderPatientDesktopSeeMoreButton({
              layout,
              onPress: () => onNavigateTab("medications"),
              palette,
            })}
          </View>

          <View style={[styles.listGroup, { gap: designTokens.spacing.sm }]}>
            {medicationRows.length > 0 ? (
              medicationRows.map((row) => (
                <Pressable
                  key={row.id}
                  onPress={() => onNavigateTab("medications")}
                  style={[
                    styles.patientDesktopMedicationCard,
                    {
                      backgroundColor: palette.white,
                      borderColor: palette.line,
                      borderRadius: 14,
                      borderWidth: 1,
                      minHeight: 62,
                      paddingHorizontal: 14,
                      paddingVertical: 10,
                    },
                  ]}
                >
                  <View style={[styles.patientDesktopMedicationIcon, { gap: designTokens.spacing.xxs }]}>
                    <View
                      style={{
                        borderColor: palette.primaryText,
                        borderRadius: 7,
                        borderWidth: 2,
                        height: 14,
                        width: 14,
                      }}
                    />
                    <Text
                      numberOfLines={2}
                      style={[
                        styles.metricLabel,
                        {
                          color: palette.primaryText,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 11,
                          fontWeight: "700",
                          textAlign: "center",
                        },
                      ]}
                    >
                      {row.routineText}
                    </Text>
                  </View>
                  <View style={[styles.patientIdentityCopy, { gap: designTokens.spacing.xxs }]}>
                    <Text
                      numberOfLines={2}
                      style={[
                        styles.navigationText,
                        {
                          color: palette.primaryText,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 12,
                          fontWeight: "400",
                          lineHeight: 16,
                        },
                      ]}
                    >
                      {row.detailText}
                    </Text>
                    <Text
                      numberOfLines={1}
                      style={[
                        styles.metricLabel,
                        {
                          color: palette.mutedText,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 11,
                        },
                      ]}
                    >
                      {row.titleText}
                    </Text>
                  </View>
                </Pressable>
              ))
            ) : (
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: palette.mutedText,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 14,
                    lineHeight: 20,
                  },
                ]}
              >
                Current medications will appear here when active orders are
                available.
              </Text>
            )}
          </View>

          <View style={[styles.listGroup, { gap: designTokens.spacing.sm }]}>
            <View style={styles.patientDesktopDashboardHeaderRow}>
              <Text
                style={[
                  styles.sectionTitle,
                  {
                    color: palette.primaryText,
                    fontFamily: layout.displayFontFamily,
                    fontSize: 24,
                    fontWeight: "600",
                    lineHeight: 30,
                  },
                ]}
              >
                Documents
              </Text>
              {renderPatientDesktopSeeMoreButton({
                layout,
                onPress: () => onNavigateTab("documents"),
                palette,
              })}
            </View>

            {documentRows.map((row) => (
              <Pressable
                key={row.id}
                onPress={() => onNavigateTab("documents")}
                style={[
                  styles.patientDesktopMedicationCard,
                  {
                    backgroundColor: palette.white,
                    borderColor: palette.line,
                    borderRadius: 14,
                    borderWidth: 1,
                    minHeight: 62,
                    paddingHorizontal: 14,
                    paddingVertical: 10,
                  },
                ]}
              >
                <View style={[styles.patientDesktopMedicationIcon, { gap: designTokens.spacing.xxs }]}>
                  <View
                    style={{
                      borderColor: palette.primaryText,
                      borderRadius: 2,
                      borderWidth: 2,
                      height: 16,
                      width: 13,
                    }}
                  />
                  <Text
                    numberOfLines={2}
                    style={[
                      styles.metricLabel,
                      {
                        color: palette.primaryText,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 11,
                        fontWeight: "700",
                        textAlign: "center",
                      },
                    ]}
                  >
                    DOC
                  </Text>
                </View>
                <View
                  style={[
                    styles.patientIdentityCopy,
                    { flex: 1, gap: designTokens.spacing.xxs, minWidth: 0 },
                  ]}
                >
                  <Text
                    numberOfLines={1}
                    style={[
                      styles.navigationText,
                      {
                        color: palette.primaryText,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 15,
                        fontWeight: "700",
                      },
                    ]}
                  >
                    {row.cells[1]?.primaryText ??
                      row.cells[0]?.primaryText ??
                      "Document"}
                  </Text>
                  <Text
                    numberOfLines={1}
                    style={[
                      styles.metricLabel,
                      {
                        color: palette.mutedText,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 11,
                      },
                    ]}
                  >
                    {row.cells[2]?.primaryText ??
                      row.cells[3]?.primaryText ??
                      "Document"}
                  </Text>
                </View>
                <View
                  style={{
                    alignItems: "center",
                    backgroundColor: patientMobilePalette.successSoft,
                    borderRadius: designTokens.radius.pill,
                    paddingHorizontal: 10,
                    paddingVertical: designTokens.spacing.xs,
                  }}
                >
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: patientMobilePalette.statusSuccessText,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 10,
                        fontWeight: "700",
                      },
                    ]}
                  >
                    Ready
                  </Text>
                </View>
              </Pressable>
            ))}
          </View>

          <View style={[styles.listGroup, { gap: designTokens.spacing.sm }]}>
            <View style={styles.patientDesktopDashboardHeaderRow}>
              <Text
                style={[
                  styles.sectionTitle,
                  {
                    color: palette.primaryText,
                    fontFamily: layout.displayFontFamily,
                    fontSize: 24,
                    fontWeight: "600",
                    lineHeight: 30,
                  },
                ]}
              >
                Calls
              </Text>
              {renderPatientDesktopSeeMoreButton({
                layout,
                onPress: () => onNavigateTab("calls"),
                palette,
              })}
            </View>

            {callRows.length > 0 ? (
              callRows.map((voiceCall) => {
                const receivedAt = parseWorkbenchDateTimeText(voiceCall.receivedAt);
                const dateText = receivedAt
                  ? formatPatientDesktopDateCell(receivedAt).primaryText
                  : "Call";
                const timeText = receivedAt
                  ? formatPatientDesktopTime(receivedAt)
                  : "Time unavailable";

                return (
                  <Pressable
                    key={voiceCall.callId}
                    onPress={() => onNavigateTab("calls")}
                    style={[
                      styles.patientDesktopMedicationCard,
                      {
                        backgroundColor: palette.white,
                        borderColor: palette.line,
                        borderRadius: 14,
                        borderWidth: 1,
                        minHeight: 62,
                        paddingHorizontal: 14,
                        paddingVertical: 10,
                      },
                    ]}
                  >
                    <View
                      style={{
                        alignItems: "center",
                        backgroundColor: palette.lightBlue,
                        borderRadius: 11,
                        height: 38,
                        justifyContent: "center",
                        width: 38,
                      }}
                    >
                      <Text style={{ fontSize: 17 }}>
                        {String.fromCodePoint(0x1f4de)}
                      </Text>
                    </View>
                    <View
                      style={[
                        styles.patientIdentityCopy,
                        { flex: 1, gap: designTokens.spacing.xxs, minWidth: 0 },
                      ]}
                    >
                      <Text
                        numberOfLines={1}
                        style={[
                          styles.navigationText,
                          {
                            color: palette.primaryText,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 15,
                            fontWeight: "700",
                          },
                        ]}
                      >
                        {voiceCall.previewText || "Phone call"}
                      </Text>
                      <Text
                        numberOfLines={1}
                        style={[
                          styles.metricLabel,
                          {
                            color: palette.mutedText,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 11,
                          },
                        ]}
                      >
                        {[dateText, timeText, voiceCall.sourceLabel]
                          .filter(Boolean)
                          .join(" - ")}
                      </Text>
                    </View>
                    <View
                      style={{
                        alignItems: "center",
                        backgroundColor: patientMobilePalette.successSoft,
                        borderRadius: designTokens.radius.pill,
                        paddingHorizontal: 10,
                        paddingVertical: designTokens.spacing.xs,
                      }}
                    >
                      <Text
                        style={[
                          styles.metricLabel,
                          {
                            color: patientMobilePalette.statusSuccessText,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 10,
                            fontWeight: "700",
                          },
                        ]}
                      >
                        Recorded
                      </Text>
                    </View>
                  </Pressable>
                );
              })
            ) : (
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: palette.mutedText,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 14,
                    lineHeight: 20,
                  },
                ]}
              >
                Phone call history will appear here when available.
              </Text>
            )}
          </View>
        </View>
      </View>
    </ScrollView>
  );
}
